document.documentElement.classList.add('js');

/* Boot. The shell (header, footer, water) is set up once; the page content is set up on
   every page change, because navigation swaps <main> in place (see initRouter). */
/* Focus rings are for the keyboard only (base.css, .is-keys): on while Tab or the arrow keys
   are moving through the page, off again at the next click or touch. */
const KEYS = ['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
addEventListener('keydown', (e) => { if (KEYS.includes(e.key)) document.documentElement.classList.add('is-keys'); }, true);
['pointerdown', 'touchstart', 'mousedown'].forEach((type) =>
  addEventListener(type, () => document.documentElement.classList.remove('is-keys'), { capture: true, passive: true }));

document.addEventListener('DOMContentLoaded', () => {
  alignBars();
  initWater();
  smoothScroll.bind();
  productGallery.bind();
  initRouter();
  initMiniBag();
  initGlow();
  initQuickAdd();
  initCurrency();
  initIntro();
  initPage();
});

/* Photos that are still on their way stay invisible and fade in when they arrive, instead of
   popping into a page that is already showing. Each photo is handled once. The home grid has
   its own reveal (initReveal), so its photos are left alone. */
function settle(root = document) {
  root.querySelectorAll('img:not([data-settled])').forEach((img) => {
    img.dataset.settled = '';
    if (img.closest('.item__media') || (img.complete && img.naturalWidth)) return;
    img.classList.add('is-loading');
    const arrive = () => {
      if (!img.classList.contains('is-loading')) return;
      img.classList.remove('is-loading');
      img.classList.add('is-arriving');
      setTimeout(() => img.classList.remove('is-arriving'), 700);
    };
    img.addEventListener('load', arrive, { once: true });
    img.addEventListener('error', arrive, { once: true });
    if (img.complete) arrive();
    setTimeout(arrive, 6000);   // never leave a photo hidden
  });
}
// Photos the visitor is about to turn to (the next product photo, the next row of the bag)
// are fetched and decoded ahead, so the turn itself is never waiting on the network.
function ahead(images) {
  images.forEach((img) => { img.loading = 'eager'; img.decode?.().catch(() => {}); });
}

/* The home grid is laid out afresh on every visit: the pieces are dealt in a new order, with
   new empty cells between them and a new choice of which ones sit lower. The deal is drawn
   once per page load (LAYOUT_SEED), so moving around the site and coming back shows the
   same arrangement — it changes when the site is opened again or refreshed. Runs before the
   reveal, so nothing is seen moving. */
const LAYOUT_SEED = Math.floor(Math.random() * 2 ** 31);
const FEW = 8;   // up to this many pieces, the grid runs four to a row instead of six
function shuffleScatter() {
  const grid = document.querySelector('[data-scatter]');
  if (!grid || grid.dataset.dealt) return;
  grid.dataset.dealt = '1';
  const items = [...grid.querySelectorAll('.item')];
  grid.classList.toggle('is-few', items.length <= FEW);   // few pieces: each is shown larger (base.css)
  if (items.length < 2) return;
  // Small seeded generator (mulberry32): the same seed always gives the same deal.
  let t = LAYOUT_SEED;
  const random = () => {
    t = (t + 0x6D2B79F5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  grid.querySelectorAll('.scatter__spacer').forEach((spacer) => spacer.remove());
  const GAPS = [0, 0, 0, 0, 1, 1, 1, 2, 2, 3];   // empty cells before a piece: mostly none or one, as in the original layout
  let lastDropped = false;
  items.forEach((item) => {
    for (let n = GAPS[Math.floor(random() * GAPS.length)]; n > 0; n--) {
      const spacer = document.createElement('div');
      spacer.className = 'scatter__spacer';
      spacer.setAttribute('aria-hidden', 'true');
      grid.append(spacer);
    }
    const dropped = !lastDropped && random() < 0.45;   // never two lowered pieces in a row
    item.classList.toggle('is-dropped', dropped);
    lastDropped = dropped;
    grid.append(item);
  });
}

/* A piece's photo rises in only once it can be drawn (base.css photo-rise): until then the
   frame waits, empty, for at most a few seconds. */
/* A description typed with "•" (or "-") at the start of lines is read as running text, not
   a list: each such line loses its mark, gains a full stop if it has none, and joins the
   paragraph before it, so the whole description is one piece of writing (the same form the
   product page gives the Summary and Details fields). */
function tidyDescription() {
  const MARK = /^\s*[•·\-–]\s+/;
  document.querySelectorAll('.product__desc').forEach((desc) => {
    if (desc.dataset.tidied) return;
    desc.dataset.tidied = '1';
    let into = null;
    [...desc.children].forEach((el) => {
      if (el.tagName !== 'P') { into = null; return; }
      if (!MARK.test(el.textContent)) { if (el.textContent.trim()) into = el; else el.remove(); return; }
      let line = el.textContent.replace(MARK, '').trim();
      if (!/[.!?]$/.test(line)) line += '.';
      if (into) { into.append(` ${line}`); el.remove(); } else { el.textContent = line; into = el; }
    });
  });
}

function productArrival() {
  const media = document.querySelector('.product__media');
  const img = media?.querySelector('img');
  if (!img || (img.complete && img.naturalWidth)) return;
  media.classList.add('is-waiting');
  const go = () => media.classList.remove('is-waiting');
  (img.decode ? img.decode() : Promise.reject()).then(go, go);
  setTimeout(go, 3000);
}

/* Mark the menu link of the page being read (base.css sets it, and the heading that is on,
   in a pale grey). Run on every page arrival. */
function markCurrent() {
  const path = (p) => p.replace(/\/$/, '');
  document.querySelectorAll('.site-header__menu a[href], .site-footer__menu a[href]').forEach((link) => {
    let on = false;
    try {
      const url = new URL(link.getAttribute('href'), location.href);
      on = url.origin === location.origin && path(url.pathname) === path(location.pathname);
    } catch (e) {}
    link.classList.toggle('is-current', on);
    if (on) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
  });
}

function initPage() {
  initBurger();
  initAnt();
  markCurrent();
  tidyDescription();
  productArrival();
  shuffleScatter();
  initFilters();
  initReveal();
  initFloat();
  smoothScroll.update();
  productGallery.update();
  initProductForm();
  initCartPage();
  initFaq();          // builds the phone's topic field, which initDropdowns then wires up
  initDropdowns();
  paintMoney();
  settle(document.querySelector('main') || document);
  // Content rows get a stagger index (--i) for the rise-in in base.css.
  document.querySelectorAll('.page__body > *, .product__info > *, .cart__line, .cart__footer > *')
    .forEach((el, i) => el.style.setProperty('--i', i));
  window.MOT.realign?.();
}

/* In-place navigation: a same-site link fetches the next page and swaps <main>, so the
   header, footer and water never reload — no flash of fonts or canvas between pages.
   The old content rises out (350ms), the new content rises in with the usual stagger. */
function initRouter() {
  if (!('fetch' in window) || !('pushState' in history)) return;
  const main = document.querySelector('main');
  if (!main) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let current = location.href;
  let entering = null;

  // Pages are fetched once and kept for a short while, so a page warmed when the finger
  // lands on its link (or the pointer rests on it) is already there when the press ends.
  const LEAVE = 350;   // ms the old page takes to fade (base.css, is-leaving) — the same on every device
  const KEEP = 20000;
  const pages = new Map();
  function load(url) {
    const hit = pages.get(url);
    if (hit && Date.now() - hit.at < KEEP) return hit.text;
    const text = fetch(url, { headers: { 'X-Requested-With': 'router' } })
      .then((res) => { if (!res.ok) throw new Error(res.status); return res.text(); });
    text.catch(() => pages.delete(url));
    pages.set(url, { at: Date.now(), text });
    return text;
  }

  // The first large photo of a page (a piece, the bag, About) is fetched and decoded before the
  // page is shown, so it is there when the page fades in instead of arriving a moment later.
  // The browser picks the same file it will pick for the real <img> (same srcset and sizes).
  const HERO = '.product__media img, .cart__media img, .cargo__photo img, .page__photos img';
  function hero(doc, limit) {
    const img = doc.querySelector('main')?.querySelector(HERO);
    if (!img || !img.getAttribute('src')) return Promise.resolve();
    const pre = new Image();
    if (img.getAttribute('sizes')) pre.sizes = img.getAttribute('sizes');
    if (img.getAttribute('srcset')) pre.srcset = img.getAttribute('srcset');
    pre.src = img.getAttribute('src');
    const done = pre.decode ? pre.decode().catch(() => {}) : new Promise((r) => { pre.onload = pre.onerror = r; });
    return Promise.race([done, new Promise((r) => setTimeout(r, limit))]);
  }

  async function go(url, push = true) {
    // The old page starts to fade at once and the new one is fetched meanwhile.
    if (push) document.documentElement.classList.add('is-leaving');
    const wait = !reduced && push ? new Promise((r) => setTimeout(r, LEAVE)) : null;
    if (!push) pages.delete(url);   // a redraw of the same page (bag, currency) needs fresh HTML
    let doc;
    try {
      doc = new DOMParser().parseFromString(await load(url), 'text/html');
    } catch (error) {
      location.href = url;   // anything odd: let the browser do a normal load
      return;
    }
    const next = doc.querySelector('main');
    if (!next) { location.href = url; return; }
    pages.delete(url);       // the bag and stock change: never show a kept page twice

    const photo = hero(doc, 900);   // at most this long for the photo; a slow one arrives as before
    if (!push) {
      document.documentElement.classList.add('is-leaving');
      if (!reduced) await new Promise((r) => setTimeout(r, LEAVE));
    } else await wait;
    await photo;

    // The URL changes first: page scripts read location (e.g. ?p=handle) when they run.
    if (push) history.pushState({ url }, '', url);
    current = url;
    document.title = doc.title;
    document.body.className = doc.body.className;
    main.innerHTML = next.innerHTML;
    // Page-scoped scripts (marked data-page) run again for the new content.
    doc.querySelectorAll('script[data-page]').forEach((old) => {
      const script = document.createElement('script');
      script.textContent = old.textContent;
      document.body.append(script);
      script.remove();
    });
    window.scrollTo(0, 0);
    initPage();
    // Arrive with a fade only (base.css, is-entering): no slide back from the leaving offset.
    document.documentElement.classList.add('is-entering');
    document.documentElement.classList.remove('is-leaving');
    clearTimeout(entering);
    entering = setTimeout(() => document.documentElement.classList.remove('is-entering'), 500);
    document.dispatchEvent(new CustomEvent('page:change', { detail: { url } }));
  }

  window.MOT.go = go;

  const routed = (link) => link && link.target !== '_blank' && !link.hasAttribute('download') && link.dataset.noRouter === undefined
    && link.origin === location.origin && /^https?:$/.test(link.protocol) && link.href !== current && !link.hash;
  // Resting on a link (or touching it) fetches its page, and that page's first photo too.
  const warmed = new Set();
  const warm = (event) => {
    const link = event.target.closest?.('a[href]');
    if (!routed(link)) return;
    const text = load(link.href);
    if (warmed.has(link.href)) return;
    warmed.add(link.href);
    text.then((html) => hero(new DOMParser().parseFromString(html, 'text/html'), 4000)).catch(() => {});
  };
  document.addEventListener('touchstart', warm, { passive: true });
  document.addEventListener('mouseover', warm, { passive: true });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target === '_blank' || link.hasAttribute('download') || link.dataset.noRouter !== undefined) return;
    if (link.origin !== location.origin || !/^https?:$/.test(link.protocol)) return;
    if (link.hash && link.pathname === location.pathname && link.search === location.search) return;
    event.preventDefault();
    if (link.href === current) return;
    go(link.href);
  });
  window.addEventListener('popstate', () => go(location.href, false));
}

/* Intro: the site opens as water and pointer only (html.is-intro, set in <head>), the
   water turning in a slow spiral. The
   first press — click, tap, key or scroll — drops a ripple there and everything surfaces:
   header items, footer items, then the page (base.css, Intro). Once per browser tab;
   add ?intro to the URL to see it again. */
function initIntro() {
  const root = document.documentElement;
  if (!root.classList.contains('is-intro')) return;
  // While the page waits, the water turns. A single drop circles the middle of the screen,
  // touching the water on every frame; the ring each touch sends out leaves a little later
  // and from a little further round than the one before, so together the rings draw one
  // spiral that winds outward and fades — and because the drop keeps circling, the spiral
  // keeps turning for as long as the page waits. Same drops as the pointer makes; it stops
  // with the first press. (window.MOT.whirl tunes it from the console.)
  let turning = true;
  const W = window.MOT.whirl = Object.assign({ radius: 12, period: 350, force: 2.2 }, window.MOT.whirl);
  const whirl = (now) => {
    if (!turning) return;
    if (window.MOT.ripple) {
      const angle = (now / W.period) * Math.PI * 2;
      window.MOT.ripple(window.innerWidth / 2 + Math.cos(angle) * W.radius, window.innerHeight / 2 + Math.sin(angle) * W.radius, W.force);
    }
    requestAnimationFrame(whirl);
  };
  requestAnimationFrame(whirl);

  const events = ['pointerdown', 'keydown', 'wheel', 'focusin'];
  const surface = (event) => {
    turning = false;
    events.forEach((type) => window.removeEventListener(type, surface, true));
    try { sessionStorage.setItem('mot-intro', '1'); } catch (e) {}
    const x = event.clientX ?? window.innerWidth / 2;
    const y = event.clientY ?? window.innerHeight / 2;
    window.MOT.ripple?.(x, y, 2.4);
    document.querySelectorAll('.site-header .filter, .site-header__menu a, .site-footer__menu > a, .site-footer__menu .currency, .site-footer__copy > *')
      .forEach((el, i) => el.style.setProperty('--e', i));
    root.classList.replace('is-intro', 'is-emerging');
    setTimeout(() => root.classList.remove('is-emerging'), 4000);
  };
  events.forEach((type) => window.addEventListener(type, surface, true));
}

/* Hover glow: a soft light behind whatever the mouse is over that can be pressed
   (base.css .bar-glow). Three lights, one per layer of the page — the header, the footer
   (each has its own stacking order), and one fixed to the screen for everything else,
   mini bag included. Targets are found by delegation, since pages are swapped in place.
   Mouse only — nothing on touch. */
function initGlow() {
  if (!window.matchMedia('(hover: hover)').matches) return;
  const PRESSABLE = 'a[href], button:not(:disabled), label.choice__option, [role="tab"]';
  const bars = [...document.querySelectorAll('.site-header, .site-footer')];

  const make = (host, fixed) => {
    const glow = document.createElement('span');
    glow.className = fixed ? 'bar-glow bar-glow--page' : 'bar-glow';
    glow.setAttribute('aria-hidden', 'true');
    host.append(glow);
    let on = false;
    return {
      show(target) {
        const origin = fixed ? { left: 0, top: 0 } : host.getBoundingClientRect();
        // The words (or the photo) themselves, not a reserved column width.
        const range = document.createRange();
        range.selectNodeContents(target);
        let r = range.getBoundingClientRect();
        if (!r.width || !r.height) r = target.getBoundingClientRect();
        const photo = !!target.querySelector('img');
        const padX = photo ? 14 : 16, padY = photo ? 14 : 5;
        if (!on) glow.classList.add('is-placing');
        glow.style.width = `${Math.round(r.width + padX * 2)}px`;
        glow.style.height = `${Math.round(r.height + padY * 2)}px`;
        glow.style.transform = `translate3d(${Math.round(r.left - origin.left - padX)}px, ${Math.round(r.top - origin.top - padY)}px, 0)`;
        if (!on) { void glow.offsetWidth; glow.classList.remove('is-placing'); }
        glow.classList.add('is-on');
        on = true;
      },
      hide() { glow.classList.remove('is-on'); on = false; },
    };
  };

  const lights = new Map(bars.map((bar) => [bar, make(bar, false)]));
  const page = make(document.body, true);
  const all = [...lights.values(), page];

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const target = event.target.closest?.(PRESSABLE);
    if (!target) { all.forEach((light) => light.hide()); return; }
    const bar = bars.find((b) => b.contains(target));
    const light = bar ? lights.get(bar) : page;
    all.forEach((other) => { if (other !== light) other.hide(); });
    light.show(target);
  });
  const hideAll = () => all.forEach((light) => light.hide());
  document.documentElement.addEventListener('pointerleave', hideAll);
  // The page light is fixed to the screen: when the page moves under it, let it go.
  window.addEventListener('scroll', () => page.hide(), { passive: true });
  window.addEventListener('wheel', () => page.hide(), { passive: true });
  document.addEventListener('page:change', hideAll);
}

/* Currency switch (footer, first item): shows the current currency; a click moves to the
   next one. On Shopify the button sits in a localization form and submits it (no script
   needed). In the preview, MOT.currency holds sample currencies and the page re-renders. */
function initCurrency() {
  const button = document.querySelector('[data-currency]');
  if (button && !window.MOT.currency && button.dataset.codes) shopCurrency(button.dataset.codes.split(','));
  const currency = window.MOT.currency;
  if (!button || !currency) return;
  button.textContent = currency.code;
  button.addEventListener('click', (event) => {
    event.preventDefault();
    button.textContent = currency.next();
    const r = button.getBoundingClientRect();
    window.MOT.ripple?.(r.left + r.width / 2, r.top + r.height / 2, 0.6);
    window.MOT.realign?.();
    window.MOT.bag?.refresh?.();
    if (window.MOT.go) window.MOT.go(location.href, false); else location.reload();
  });
}

/* Shopify shops that check out in one currency only: the switch shows prices in other
   currencies for reference (Shopify's public exchange rates); checkout stays in the shop's
   currency. codes = the shop's currency first, then the ones to offer. */
function shopCurrency(codes) {
  const KEY = 'mot-currency';
  let index = 0;
  try { index = Math.max(0, codes.indexOf(localStorage.getItem(KEY))); } catch (e) {}
  window.MOT.currency = {
    get code() { return codes[index]; },
    next() {
      index = (index + 1) % codes.length;
      try { localStorage.setItem(KEY, codes[index]); } catch (e) {}
      return codes[index];
    },
  };
  // First visit (nothing chosen yet): start in the currency of the country the visitor is in,
  // when it is one of the currencies on the switch — Korea ₩, Japan ¥, the euro countries €,
  // the United States $. Anywhere else stays in the shop's own currency. Shopify tells the
  // country; once the visitor presses the switch, their own choice is kept instead.
  let chosen = null;
  try { chosen = localStorage.getItem(KEY); } catch (e) {}
  if (!chosen) {
    const EURO = 'AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK'.split(' ');
    fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((data) => {
        const country = data?.detected_values?.country?.handle;
        const local = { KR: 'KRW', JP: 'JPY', US: 'USD', TH: 'THB' }[country] || (EURO.includes(country) ? 'EUR' : null);
        const at = codes.indexOf(local);
        if (at < 0 || at === index) return;
        index = at;
        document.querySelectorAll('.currency[data-currency]').forEach((button) => { button.textContent = codes[index]; });
        paintMoney();
        window.MOT.realign?.();
        window.MOT.bag?.refresh?.();
      })
      .catch(() => {});
  }
  const script = document.createElement('script');
  script.src = '/services/javascripts/currencies.js';   // the shop's own copy of Shopify's rates
  script.onload = () => paintMoney();
  document.head.append(script);
}

/* Money in the shop's own format (layout/theme.liquid passes it in, e.g. "฿{{amount}}"),
   or converted for reference when the footer switch is on another currency. */
function money(cents) {
  const shop = window.MOT.shopCurrency, code = window.MOT.currency?.code || shop;
  const rates = window.Currency?.rates;
  if (code !== shop && rates?.[shop] && rates?.[code]) {
    const amount = ((cents / 100) * rates[shop]) / rates[code];
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, maximumFractionDigits: amount >= 1000 ? 0 : 2 }).format(amount);
  }
  const number = (decimals, locale) => (cents / 100).toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const kinds = { amount: () => number(2, 'en-US'), amount_no_decimals: () => number(0, 'en-US'), amount_with_comma_separator: () => number(2, 'de-DE'), amount_no_decimals_with_comma_separator: () => number(0, 'de-DE') };
  return (window.MOT.moneyFormat || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, kind) => (kinds[kind] || kinds.amount)()).replace(/<[^>]*>/g, '');
}

/* Prices printed by Liquid carry data-cents; rewrite them in the chosen currency. */
function paintMoney() {
  if (!window.MOT.shopCurrency) return;
  document.querySelectorAll('[data-cents]').forEach((el) => { if (el.tagName !== 'OPTION') el.textContent = money(Number(el.dataset.cents)); });
  const shop = window.MOT.shopCurrency, code = window.MOT.currency?.code || shop;
  document.querySelectorAll('[data-currency-note]').forEach((el) => { el.hidden = code === shop; });
}

/* Mini bag: the Bag link opens a small panel under the header (Objects line → right
   margin) instead of leaving the page. Lines rise in with a stagger; Escape, the scrim or
   ( ) Close dismiss it. Data comes from MOT.cartProvider (the preview's sample bag) or,
   on Shopify, from /cart.js and /cart/change.js. */
function initMiniBag() {
  const link = document.querySelector('.bag-link');
  if (!link) return;

  // Shopify's CDN resizes on request: never download the original for a thumbnail.
  const sized = (url, width) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : url);
  const shopify = {
    checkoutUrl: '/checkout',
    bagUrl: link.getAttribute('href'),
    productUrl: (line) => (line && line.url) || '/',   // a line of the Shopify bag carries its own address
    // The shop's own money format (layout/theme.liquid passes it in), e.g. "฿{{amount}}".
    format: (cents) => money(cents),
    async fetchCart() { return (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json(); },
    async lines() {
      const cart = await this.fetchCart();
      return cart.items.map((i) => ({ handle: i.key, title: i.variant_title ? `${i.product_title} — ${i.variant_title}` : i.product_title, type: i.variant_title, qty: i.quantity, image: sized(i.image, 600), alt: i.product_title, line_price: i.final_line_price, url: i.url }));
    },
    async count() { return (await this.fetchCart()).item_count; },
    async subtotal() { return (await this.fetchCart()).total_price; },
    // Pieces offered in the empty bag: the first few that are in stock.
    async picks() {
      const data = await (await fetch('/collections/all/products.json?limit=12', { headers: { Accept: 'application/json' } })).json();
      return data.products.map((product) => ({ product, variant: product.variants.find((v) => v.available) }))
        .filter((x) => x.variant && x.product.images[0] && !(x.product.tags || []).includes('hidden')).slice(0, 4)
        .map(({ product, variant }) => ({ id: variant.id, title: product.title, image: sized(product.images[0].src, 600), url: `/products/${product.handle}` }));
    },
    async add(id, qty = 1) {
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id, quantity: qty }) });
      if (!res.ok) throw new Error(res.status);
      await trimBag();
    },
    async set(key, qty) {
      qty = Math.min(qty, stockOf(parseInt(key, 10)));   // a line's key starts with its variant id
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) });
    },
  };
  const provider = () => window.MOT.cartProvider || shopify;
  window.MOT.cart = provider;   // the bag page uses the same source (initCartPage)

  const root = document.createElement('div');
  root.className = 'minibag';
  root.hidden = true;
  root.innerHTML = `
    <div class="minibag__scrim" data-close></div>
    <aside class="minibag__panel" role="dialog" aria-modal="true" aria-label="Bag" tabindex="-1">
      <p class="minibag__head"><span>(Bag)</span><span data-count-label></span></p>
      <ul class="minibag__lines"></ul>
      <p class="minibag__total"><span>Subtotal</span><span data-total></span></p>
      <div class="minibag__actions">
        <a class="text-button" data-bag-url href="#"><span class="filter__mark" aria-hidden="true">(<span class="filter__dot">●</span>)</span><span>View bag</span></a>
        <a class="text-button" data-checkout-url href="#" data-no-router><span class="filter__mark" aria-hidden="true">(<span class="filter__dot">●</span>)</span><span>Checkout</span></a>
        <button class="text-button" type="button" data-close><span class="filter__mark" aria-hidden="true">(<span class="filter__dot">●</span>)</span><span>Close</span></button>
      </div>
    </aside>`;
  document.body.append(root);
  const panel = root.querySelector('.minibag__panel');
  const list = root.querySelector('.minibag__lines');
  let isOpen = false;

  async function render(splash = false) {
    const p = provider();
    root.querySelector('[data-bag-url]').href = p.bagUrl;
    root.querySelector('[data-checkout-url]').href = p.checkoutUrl;
    const lines = await p.lines();
    const count = await p.count();
    root.querySelector('[data-count-label]').textContent = count ? `${count} ${count === 1 ? 'piece' : 'pieces'}` : '';
    list.innerHTML = lines.map((line, i) => `
      <li class="minibag__line" style="--i:${i}">
        <a class="minibag__thumb" href="${line.url || (typeof p.productUrl === 'function' ? p.productUrl(line.handle ?? line) : '#')}">
          <img src="${line.image}" alt="">
        </a>
        <span class="minibag__title">${numero(line.title)}</span>
        <span class="minibag__meta">${line.qty > 1 ? `<span class="minibag__count">×${line.qty}</span>` : ''}${p.format(line.line_price)}</span>
        <button class="minibag__remove" type="button" data-remove="${line.handle}">Remove</button>
      </li>`).join('');
    // Empty bag: a few pieces from the shop instead, each with a + that puts it in the bag.
    let picks = [];
    if (!lines.length) {
      try { picks = (await p.picks?.()) || []; } catch (error) { picks = []; }
      list.innerHTML = picks.map((pick, i) => `
      <li class="minibag__line minibag__line--pick" style="--i:${i}">
        <span class="minibag__thumb">
          <a href="${pick.url}"><img src="${pick.image}" alt=""></a>
          <button type="button" class="cart__add minibag__add" data-add="${pick.id}" aria-label="Add ${pick.title} to bag"><svg class="cart__add-icon" viewBox="0 0 48 48" aria-hidden="true"><path d="M24 4v40M4 24h40" fill="none" stroke="currentColor" stroke-width="1" vector-effect="non-scaling-stroke"/></svg></button>
        </span>
        <span class="minibag__title">${numero(pick.title)}</span>
      </li>`).join('') || '<li class="minibag__line minibag__line--empty">Your bag is empty.</li>';
    }
    root.classList.toggle('is-empty', !lines.length);
    // Fewer pieces get bigger tiles, so a bag with one or two things doesn't look empty.
    list.dataset.count = String(Math.min(lines.length || picks.length, 4));
    settle(list);
    root.querySelector('[data-total]').textContent = p.format(await p.subtotal());
    window.MOT.setBagCount(count);
    // Each piece is set down on the water: a ripple under it, in step with its rise-in.
    if (splash && window.MOT.ripple) {
      list.querySelectorAll('.minibag__thumb').forEach((thumb, i) => {
        setTimeout(() => {
          const r = thumb.getBoundingClientRect();
          window.MOT.ripple(r.left + r.width / 2, r.bottom - 6, 1.4);
        }, 350 + i * 90);
      });
    }
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    root.hidden = false;
    void root.offsetHeight;   // commit the unhidden state, so the open transition runs
    root.classList.add('is-open');
    link.setAttribute('aria-expanded', 'true');
    document.body.classList.add('bag-open');
    render(true).then(() => panel.focus({ preventScroll: true }));
  }
  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    document.body.classList.remove('bag-open');
    link.setAttribute('aria-expanded', 'false');
    setTimeout(() => { if (!isOpen) root.hidden = true; }, 500);
    link.focus({ preventScroll: true });
  }

  link.setAttribute('aria-haspopup', 'dialog');
  link.setAttribute('aria-expanded', 'false');
  link.addEventListener('click', (event) => {
    if (event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    isOpen ? close() : open();
  }, true);
  root.addEventListener('click', async (event) => {
    if (event.target.closest('[data-close]')) return close();
    const remove = event.target.closest('[data-remove]');
    if (remove) { await provider().set(remove.dataset.remove, 0); render(); }
    // + on a piece in the empty bag: into the bag it goes, and the bag shows it.
    const add = event.target.closest('[data-add]');
    if (add && add.getAttribute('aria-busy') !== 'true') {
      add.setAttribute('aria-busy', 'true');
      const r = add.closest('li').getBoundingClientRect();
      window.MOT.ripple?.(r.left + r.width / 2, r.top + r.height / 2, 1.6);
      try {
        await provider().add(add.dataset.add);
        await render(true);
        // On the bag page itself, redraw it too.
        if (document.querySelector('.cart') && window.MOT.go) window.MOT.go(location.href, false);
      } catch (error) { add.removeAttribute('aria-busy'); }
    }
  });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  document.addEventListener('page:change', close);

  // A bag filled before a piece sold (or before this limit existed) is brought back within stock.
  if (provider() === shopify) trimBag().then((over) => { if (over.length) window.MOT.bag?.refresh(); });
  window.MOT.bag = { open, close, refresh: () => { if (isOpen) render(); else provider().count().then((n) => window.MOT.setBagCount(n)); } };
  // Keep the header count honest on load (the preview's sample bag lives in sessionStorage).
  provider().count().then((n) => window.MOT.setBagCount(n)).catch(() => {});
}

/* Shared helpers. MOT.page(fn) runs page code now, or on DOMContentLoaded if the document
   is still loading — so inline page scripts work on first load and after a router swap. */
window.MOT = Object.assign(window.MOT || {}, {
  page(fn) { document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn(); },
  // Bag count with an odometer step: call MOT.setBagCount(n) wherever the cart changes.
  setBagCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      // The element may be mid-animation (two digits inside): compare with the value it is
      // heading to, and settle any pending change before starting the next.
      const current = el.dataset.value ?? el.textContent.trim();
      if (current === String(n)) return;
      clearTimeout(el._settle);
      el.dataset.value = String(n);
      el.innerHTML = `<span class="is-out">${current}</span><span class="is-in">${n}</span>`;
      el._settle = setTimeout(() => { el.textContent = String(n); }, 600);
    });
  },
});

const OSMO_EASE = 'cubic-bezier(.625, .05, 0, 1)';

function pageRect(el) {
  const r = el.getBoundingClientRect();
  return { x: r.left + window.scrollX, y: r.top + window.scrollY };
}

/* Line the header and footer up item for item (All over the currency switch … About over ©year,
   Bag over the studio name). Both bars are space-between; giving each pair one shared
   width makes their gaps, and so their left edges, identical. Pairs count from the right. */
function alignBars() {
  // Only what is on screen takes part: on phones About sits in the footer (not the header)
  // and ©year / the studio name drop to their own line.
  const shown = (el) => getComputedStyle(el).display !== 'none';
  const top = () => [...document.querySelectorAll('.site-header .filter, .site-header__menu a')];
  const bottom = (wide) => [...document.querySelectorAll(wide
    ? '.site-footer__menu a, .site-footer__menu .currency, .site-footer__copy > *'
    : '.site-footer__menu a, .site-footer__menu .currency')];
  const apply = () => {
    const wide = window.matchMedia('(min-width: 750px)').matches;
    [...top(), ...bottom(true)].forEach((el) => { el.style.width = ''; el.style.display = ''; });
    const a = top().filter(shown), b = bottom(wide).filter(shown);
    // Wide screens, five words above and six below (All · two headings · About · Bag over
    // currency · three links · ©year · name): both bars are set on one grid of four columns
    // (base.css, is-grid4), so they need no shared widths. Any other count keeps the old spread.
    const grid4 = wide && (a.length === 5 || a.length === 3) && b.length === 6;   // five words, or three (All · About · Bag) when the headings are a row on the shop page
    document.documentElement.classList.toggle('is-grid4', grid4);
    // Phones: no shared widths — each bar spreads its own words with equal gaps between them.
    const n = wide && !grid4 ? Math.min(a.length, b.length) : 0;
    for (let i = 1; i <= n; i++) {
      const x = a[a.length - i], y = b[b.length - i];
      const w = Math.ceil(Math.max(x.getBoundingClientRect().width, y.getBoundingClientRect().width));
      [x, y].forEach((el) => { el.style.display = 'inline-block'; el.style.width = `${w}px`; });
    }
    // Every page hangs its columns from the header items' left edges (see --hx1… in base.css).
    // The layouts count on seven items (All, four types, About, Bag); with any other number the
    // seven column edges are spread evenly between the first item and the last.
    const edges = a.map((el) => el.getBoundingClientRect().left);
    const COLS = 7;
    const xs = edges.length === COLS || edges.length < 2 ? edges
      : Array.from({ length: COLS }, (_, i) => edges[0] + ((edges[edges.length - 1] - edges[0]) * i) / (COLS - 1));
    // With fewer items the middle line (the fourth) is set on the middle item of the menu itself
    // (Snail of All · Thai Sofa · Snail · About · Bag; About when the headings are hidden), so
    // text columns that hang from it — a piece's details, the bag, the About text — sit exactly
    // under a word.
    if (edges.length !== COLS && xs.length === COLS && a.length >= 3) {
      xs[3] = a[Math.floor(a.length / 2)].getBoundingClientRect().left;
    }
    // On the grid of four the lines are the four columns themselves — All, the two headings,
    // About — with the half-way lines between them, and Bag's own left edge last.
    if (grid4) {
      // The four lines are read off the footer (currency · three links), which always fills them;
      // the menu may leave the two middle columns empty.
      const [l1, l2, l3, l4] = b.slice(0, 4).map((el) => el.getBoundingClientRect().left), bag = edges[edges.length - 1];
      xs.splice(0, xs.length, l1, l2, (l2 + l3) / 2, l3, (l3 + l4) / 2, l4, bag);
    }
    xs.forEach((x, i) => document.documentElement.style.setProperty(`--hx${i + 1}`, `${Math.round(x)}px`));
  };
  apply();
  document.fonts?.ready.then(apply);
  window.addEventListener('resize', apply);
  const bag = document.querySelector('.bag-link');
  if (bag) new MutationObserver(apply).observe(bag, { childList: true, subtree: true, characterData: true });
  window.MOT.realign = apply;
}

/* Build filter buttons from product types in the grid, then filter on click. */
function initFilters() {
  initSnailMap();
  if (!document.querySelector('[data-scatter]')) { window.MOT.snailMap(false); window.MOT.subPick?.remove(); }   // the map belongs to the Snail heading of the grid
  const nav = document.querySelector('[data-filters]');
  if (!nav) return;
  const grid = document.querySelector('[data-scatter]');
  const home = nav.dataset.home || '/';
  // Start from a clean "All" button (cloneNode drops old listeners) — pages are swapped in place.
  nav.replaceChildren(nav.querySelector('.filter').cloneNode(true));
  nav.querySelector('.filter').lastElementChild.textContent = 'All';   // a piece's page may have renamed it Back

  // The type list comes from the grid when there is one; other pages get it from the
  // header (data-types="handle:Label,…", written by Liquid / the preview shell).
  // data-fixed: the headings are set in the theme settings and keep their order.
  const fixed = nav.hasAttribute('data-fixed');
  const types = new Map();
  const items = grid ? [...grid.querySelectorAll('.item')] : [];
  if (!fixed) items.forEach((item) => {
    if (item.dataset.type) types.set(item.dataset.type, item.dataset.typeLabel || item.dataset.type);
  });
  // Remember the grid's types on the header, so pages swapped in later still get the buttons.
  if (grid && !fixed && types.size) nav.dataset.types = [...types].map(([v, l]) => `${v}:${l}`).join(',');
  if ((!grid || fixed) && nav.dataset.types) {
    nav.dataset.types.split(',').forEach((pair) => {
      const [value, label] = pair.split(':');
      if (value) types.set(value.trim(), (label || value).trim());
    });
  }

  // Row mode (theme settings, "Show the headings as one row"): the menu keeps only All; on the
  // shop page every heading is offered in one large row — All first — and a press filters the
  // grid in place. The row is the same element as the row under a heading with several words.
  const rowTypes = new Map();
  (nav.dataset.rowTypes || '').split(',').forEach((pair) => {
    const [value, label] = pair.split(':');
    if (value && value.trim()) rowTypes.set(value.trim(), (label || value).trim());
  });
  const rowMode = rowTypes.size > 0;
  const known = rowMode ? rowTypes : types;

  (fixed ? [...types] : [...types].sort((a, b) => a[1].localeCompare(b[1]))).forEach(([value, label]) => {
    const button = nav.querySelector('.filter').cloneNode(true);
    button.dataset.filter = value;
    button.lastElementChild.textContent = label;
    nav.append(button);
  });

  const buttons = [...nav.querySelectorAll('.filter')];

  // Away from the grid, a filter is a link home that carries the chosen type.
  if (!grid) {
    // On a piece's own page the menu's All reads Back and returns to the heading that piece sits
    // under (Thai Sofa, Snail …), worked out the same way as on the shop page: its type or a
    // tag first, then its name. A piece under no heading goes back to All.
    const piece = document.querySelector('.product[data-piece-name]');
    let back = null;
    if (piece) {
      const type = (piece.dataset.pieceType || '').trim().toLowerCase();
      const tags = (piece.dataset.pieceTags || '').split('|').map((t) => t.trim().toLowerCase());
      const name = (piece.dataset.pieceName || '').toLowerCase();
      const list = [...known].map(([value, label]) => [value, label.toLowerCase()]);
      back = (list.find(([, label]) => label === type || tags.includes(label)) || list.find(([, label]) => name.includes(label)) || ['all'])[0];
      const all = buttons.find((button) => button.dataset.filter === 'all');
      if (all) all.lastElementChild.textContent = 'Back';
    }
    buttons.forEach((button) => {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', () => {
        const value = back && button.dataset.filter === 'all' ? back : button.dataset.filter;
        const url = value === 'all' ? home : `${home}${home.includes('?') ? '&' : '?'}type=${encodeURIComponent(value)}`;
        if (window.MOT.go) window.MOT.go(new URL(url, location.href).href); else location.href = url;
      });
    });
    return;
  }

  // A heading with nothing under it yet says so, instead of leaving an empty page.
  const note = () => {
    let empty = grid.querySelector('.scatter__empty');
    if (!empty) {
      empty = document.createElement('p');
      empty.className = 'scatter__empty';
      empty.textContent = 'Coming soon.';
      grid.append(empty);
    }
    empty.hidden = items.some((item) => !item.hidden);
  };
  // A heading that takes in several words ("Things = Thai Sofa / Accessories" in the theme
  // settings) offers those words under it, in the same row as Find Snail / Snail Map: one is
  // chosen at a time (the first to begin with) and only its pieces show.
  const subs = new Map();
  (nav.dataset.subs || '').split(';').forEach((entry) => {
    const [handle, words] = entry.split(':');
    const list = (words || '').split('/').map((w) => w.trim()).filter(Boolean);
    if (handle && list.length > 1) subs.set(handle.trim(), list);
  });
  const slug = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const chosenSub = (window.MOT.subChoice = window.MOT.subChoice || {});
  const subPick = window.MOT.subPick || (window.MOT.subPick = Object.assign(document.createElement('p'), { className: 'snailpick' }));
  const rowShow = (value) => {
    subPick.innerHTML = [['all', 'All'], ...rowTypes].map(([v, label]) => `
      <button type="button" class="filter${v === value ? ' is-active' : ''}" data-row-pick="${v}" aria-pressed="${v === value}"><span class="filter__mark" aria-hidden="true">(<span class="filter__dot">●</span>)</span><span>${label}</span></button>`).join('');
    subPick.onclick = (event) => {
      const button = event.target.closest('[data-row-pick]');
      if (button && !button.classList.contains('is-active')) apply(button.dataset.rowPick);
    };
    // The row is set in the menu's own small type, on the menu's line (base.css, is-menu): it
    // takes the place of the menu's single All while the shop page is open.
    subPick.classList.add('is-menu');
    document.body.append(subPick);
    grid.classList.add('has-menu-row');
  };
  const subFilter = (value) => {
    if (rowMode) { rowShow(value); return; }
    subPick.classList.remove('is-menu');
    const words = subs.get(value);
    grid.classList.toggle('has-pick', !!words);
    if (!words) { subPick.remove(); return; }
    const now = words.includes(chosenSub[value]) ? chosenSub[value] : words[0];
    chosenSub[value] = now;
    subPick.innerHTML = words.map((word) => `
      <button type="button" class="filter${word === now ? ' is-active' : ''}" data-sub-pick="${word}" aria-pressed="${word === now}"><span class="filter__mark" aria-hidden="true">(<span class="filter__dot">●</span>)</span><span>${word}</span></button>`).join('');
    subPick.onclick = (event) => {
      const button = event.target.closest('[data-sub-pick]');
      if (!button || button.dataset.subPick === chosenSub[value]) return;
      chosenSub[value] = button.dataset.subPick;
      // Cross-fade, like the two Snail pages: out, change, back to the top, in.
      grid.style.transition = 'opacity 0.26s ease'; grid.style.opacity = '0';
      subPick.querySelectorAll('[data-sub-pick]').forEach((b) => { const on = b === button; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on); });
      setTimeout(() => {
        apply(value, false);
        const handled = !grid.dispatchEvent(new CustomEvent('scatter:filtered', { cancelable: true }));
        if (!handled) window.scrollTo(0, 0);
        void grid.offsetHeight;
        grid.style.transition = 'opacity 0.55s cubic-bezier(.625, .05, 0, 1)'; grid.style.opacity = '';
        setTimeout(() => { grid.style.transition = ''; }, 650);
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 270);
    };
    document.body.append(subPick);
    items.forEach((item) => { if (item.dataset.type === value) item.hidden = item.dataset.sub !== slug(now); });
  };
  const apply = (value, animate = true) => {
      buttons.forEach((b) => {
        const on = b.dataset.filter === value;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on);
      });
      if (!animate) {
        grid.classList.toggle('is-filtered', value !== 'all');
        grid.classList.toggle('is-snail', value === 'snail' && !!window.MOT.snailMapOn);
        window.MOT.snailMap?.(value === 'snail', grid);
        items.forEach((item) => { item.hidden = value !== 'all' && item.dataset.type !== value; });
        subFilter(value);
        note();
        return;
      }
      // FLIP: record where each item sits, change the layout, then glide from old to new.
      const before = new Map(items.filter((i) => !i.hidden).map((i) => [i, pageRect(i)]));
      grid.classList.toggle('is-filtered', value !== 'all');
        grid.classList.toggle('is-snail', value === 'snail' && !!window.MOT.snailMapOn);
        window.MOT.snailMap?.(value === 'snail', grid);
      items.forEach((item) => {
        item.hidden = value !== 'all' && item.dataset.type !== value;
      });
      subFilter(value);
      note();
      items.filter((i) => !i.hidden).forEach((item, index) => {
        const from = before.get(item);
        item.getAnimations().filter((a) => !(a instanceof CSSAnimation)).forEach((a) => a.cancel()); // keep the float running
        if (from) {
          const to = pageRect(item);
          item.animate(
            [{ transform: `translate(${from.x - to.x}px, ${from.y - to.y}px)` }, { transform: 'none' }],
            { duration: 900, easing: OSMO_EASE }
          );
        } else {
          item.animate(
            [{ opacity: 0, transform: 'translateY(24px)' }, { opacity: 1, transform: 'none' }],
            { duration: 700, delay: index * 36, easing: OSMO_EASE, fill: 'backwards' }
          );
        }
      });
      // The smooth scroller (if running) takes the page to the top itself.
      const handled = !grid.dispatchEvent(new CustomEvent('scatter:filtered', { cancelable: true }));
      if (!handled) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  buttons.forEach((button) => button.addEventListener('click', () => apply(button.dataset.filter)));

  // Arriving from another page with ?type=… : start already filtered.
  const wanted = new URLSearchParams(location.search).get('type');
  apply(wanted && known.has(wanted) ? wanted : 'all', false);

}

/* Masked reveal as items enter the viewport; items arriving together are staggered. */
function initReveal() {
  const items = document.querySelectorAll('.scatter .item');
  if (!('IntersectionObserver' in window)) {
    items.forEach((item) => item.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries
      .filter((entry) => entry.isIntersecting)
      .forEach((entry, index) => {
        const item = entry.target;
        item.style.setProperty('--reveal-delay', `${index * 36}ms`);
        observer.unobserve(item);
        // Open the mask only once the photo is ready to paint: a picture that arrives
        // halfway through its own reveal pops in and reads as a stutter.
        const img = item.querySelector('img');
        let shown = false;
        // After the mask has opened it comes off (base.css, is-settled) — but not while the
        // intro still holds the photos closed, or they would appear without their reveal.
        const settleMask = () => {
          const root = document.documentElement;
          if (root.classList.contains('is-intro') || root.classList.contains('is-emerging')) { setTimeout(settleMask, 1000); return; }
          setTimeout(() => item.classList.add('is-settled'), 2600);
        };
        const show = () => {
          if (shown) return;
          shown = true;
          // On a slow connection the frame may open before its photo has come: the photo then
          // fades in where it stands when it arrives, instead of popping in (base.css is-waiting).
          if (img && !(img.complete && img.naturalWidth)) {
            item.classList.add('is-waiting');
            const arrive = () => item.classList.remove('is-waiting');
            img.addEventListener('load', arrive, { once: true });
            img.addEventListener('error', arrive, { once: true });
          }
          item.classList.add('is-visible');
          settleMask();
        };
        if (!img) { show(); return; }
        img.loading = 'eager';
        (img.decode ? img.decode() : Promise.reject()).then(show, show);
        setTimeout(show, 3000);   // a slow photo holds its frame closed this long at most
      });
  }, { threshold: 0.2 });
  items.forEach((item) => observer.observe(item));
}

/* Give every piece its own gentle drift so they never move in step. */
function initFloat() {
  const rand = (min, max) => min + Math.random() * (max - min);
  document.querySelectorAll('.scatter .item').forEach((item) => {
    const duration = rand(9, 15);
    item.style.setProperty('--fx', rand(2, 5).toFixed(1));   // small sideways drift, so pieces stay on their column lines
    item.style.setProperty('--fy', rand(5, 11).toFixed(1));
    item.style.setProperty('--fr', rand(0.5, 1.3).toFixed(2));
    item.style.setProperty('--fdur', `${duration.toFixed(1)}s`);
    item.style.setProperty('--fdelay', `${(-Math.random() * duration).toFixed(1)}s`);
  });
  // Mark the pieces well off screen: on phones and tablets those wait (base.css). Pieces are
  // only ever paused by this, never started — if it does not run, everything simply drifts.
  if (!('IntersectionObserver' in window)) return;
  const far = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.target.classList.toggle('is-far', !entry.isIntersecting));
  }, { rootMargin: '200px 0px' });
  document.querySelectorAll('.scatter .item').forEach((item) => far.observe(item));
}

/* Smooth wheel scrolling on the grid page (desktop only).
   Wheel / trackpad / keys move a target and the page glides after it (lerp).
   <main> is fixed and moved with translate3d, so each frame is a compositor-only move.
   Bound once; update() turns it on or off for the current page. */
const smoothScroll = (() => {
  const LERP = 9; // how tightly the page follows its target (per second) — lower = floatier
  let active = false, content = null, grid = null;
  let pos = 0, target = 0, last = null, frame = null;

  const maxScroll = () => Math.max(0, content.scrollHeight - window.innerHeight);

  function step(now) {
    const dt = last === null ? 1 / 60 : Math.min((now - last) / 1000, 0.1);
    last = now;
    target = Math.max(0, Math.min(target, maxScroll()));
    pos += (target - pos) * (1 - Math.exp(-dt * LERP));
    if (Math.abs(target - pos) < 0.05) pos = target;
    content.style.transform = `translate3d(0, ${(-pos).toFixed(2)}px, 0)`;
    frame = pos === target ? null : requestAnimationFrame(step);
  }
  function scrollBy(delta) {
    if (!active) return;
    target += delta;
    if (!frame) { last = null; frame = requestAnimationFrame(step); }
  }

  function bind() {
    window.addEventListener('wheel', (event) => {
      if (!active || event.ctrlKey) return; // pinch-zoom
      event.preventDefault();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      scrollBy(event.deltaY * unit);
    }, { passive: false });
    // Keyboard scrolling, since the page no longer scrolls natively.
    window.addEventListener('keydown', (event) => {
      if (!active || event.target.closest?.('input, textarea, select, [contenteditable]')) return;
      const page = window.innerHeight * 0.85;
      const moves = { ArrowDown: 90, ArrowUp: -90, PageDown: page, PageUp: -page, ' ': event.shiftKey ? -page : page };
      if (event.key in moves) { event.preventDefault(); scrollBy(moves[event.key]); }
      else if (event.key === 'Home') { event.preventDefault(); scrollBy(-target); }
      else if (event.key === 'End') { event.preventDefault(); scrollBy(maxScroll() - target); }
    });
    // Tabbing to a piece off screen brings it into view.
    document.addEventListener('focusin', (event) => {
      if (!active || !event.target.closest('[data-scatter]')) return;
      const rect = event.target.getBoundingClientRect();
      if (rect.top < 60 || rect.bottom > window.innerHeight - 60) scrollBy(rect.top - window.innerHeight / 3);
    });
    window.addEventListener('resize', () => scrollBy(0));
    // Crossing the phone / wide breakpoint (a window dragged narrow, a tablet turned): the page
    // is drawn again, so the scrolling, the product photos and the bars all start from the
    // layout that now applies instead of keeping the other one's state.
    const isWide = () => window.matchMedia('(min-width: 750px)').matches;
    let wide = isWide();
    window.addEventListener('resize', () => {
      if (isWide() === wide) return;
      wide = isWide();
      update();   // native scrolling is back at once, whatever the redraw does
      if (window.MOT.go) window.MOT.go(location.href, false); else location.reload();
    });
  }

  function update() {
    cancelAnimationFrame(frame); frame = null;
    if (content) content.style.transform = '';
    grid = document.querySelector('[data-scatter]');
    // Mouse and trackpad only: a touch screen (a tablet held sideways is as wide as a laptop)
    // has no wheel to drive this, so there the page scrolls natively.
    active = !!grid
      && window.matchMedia('(min-width: 750px)').matches
      && window.matchMedia('(hover: hover) and (pointer: fine)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.documentElement.classList.toggle('has-virtual-scroll', active);
    if (!active) { content = null; return; }
    content = grid.closest('main') || grid.parentElement;
    window.scrollTo(0, 0);
    pos = target = 0;
    content.style.transform = 'translate3d(0, 0, 0)';
    // Filtering: glide back to the top.
    grid.addEventListener('scatter:filtered', (event) => { event.preventDefault(); scrollBy(-target); });
  }

  return { bind, update };
})();

/* Product photos, driven by scroll (desktop) — an osmo-style masked slide.
   The photos sit in one frame. One scroll gesture moves to the next photo: its mask opens
   from the bottom edge while the photo inside settles from a 1.18 zoom, and the outgoing
   photo drifts up at a slower rate under it (parallax). Timed, on the osmo ease
   (cubic-bezier(.625,.05,0,1)) — hard entry, long exhale. Nothing else on the page moves,
   and nothing is clickable. The stack loops in both directions.
   On phones the photos simply stack and the page scrolls.
   Bound once; update() picks up the product on the current page. */
const productGallery = (() => {
  const NUDGE = 24;        // wheel travel that counts as "next photo"
  const DURATION = 1150;   // ms per photo
  let media = null, images = [];
  let pos = 0, from = 0, target = 0, startTime = 0, span = DURATION, frame = null;
  let sum = 0, locked = false, quietTimer = null, lastTrigger = 0;

  const enabled = () => window.matchMedia('(min-width: 750px)').matches;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // cubic-bezier(.625, .05, 0, 1) solved for y at time x
  function osmoEase(x) {
    const x1 = 0.625, y1 = 0.05, x2 = 0, y2 = 1;
    const bez = (t, a, c) => 3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * c + t * t * t;
    let lo = 0, hi = 1, t = x;
    for (let i = 0; i < 24; i++) { t = (lo + hi) / 2; if (bez(t, x1, x2) < x) lo = t; else hi = t; }
    return bez(t, y1, y2);
  }

  function render() {
    // The stack loops: position runs on forever and photos are taken modulo the count.
    const n = images.length;
    const floor = Math.floor(pos);
    const t = pos - floor;               // 0 → 1 between one photo and the next
    const base = ((floor % n) + n) % n;
    const incoming = (base + 1) % n;
    images.forEach((img, i) => {
      if (i === base) {
        // outgoing: drifts up slowly under the incoming mask (parallax)
        img.style.visibility = 'visible';
        img.style.zIndex = 1;
        img.style.clipPath = 'none';
        img.style.transform = t ? `translate3d(0, ${(-t * 14).toFixed(3)}%, 0)` : '';
        img.style.opacity = 1;
      } else if (i === incoming && t > 0) {
        // incoming: mask opens from the bottom; the photo settles from a zoom
        img.style.visibility = 'visible';
        img.style.zIndex = 2;
        img.style.clipPath = `inset(${((1 - t) * 100).toFixed(3)}% 0 0 0)`;
        img.style.transform = `scale(${(1.18 - 0.18 * t).toFixed(4)})`;
        img.style.opacity = 1;
      } else {
        img.style.visibility = 'hidden';
        img.style.clipPath = 'none';
        img.style.transform = '';
      }
    });
  }

  function step(now) {
    const k = Math.min((now - startTime) / span, 1);
    pos = from + (target - from) * osmoEase(k);
    render();
    frame = k < 1 ? requestAnimationFrame(step) : null;
    if (k >= 1) { pos = target; render(); }
  }
  function moveTo(value) {
    const next = Math.round(value);   // unbounded: past the last photo comes the first again
    if (next === target && frame) return;
    if (next === target && pos === target) return;
    target = next;
    if (reduced()) { pos = target; render(); return; }
    from = pos;
    span = DURATION * Math.min(1, Math.max(0.55, Math.abs(target - from)));
    startTime = performance.now();
    if (!frame) frame = requestAnimationFrame(step);
  }

  function bind() {
    // When the text beside the photos is longer than the screen, the page has to scroll: then
    // the wheel only turns photos while the pointer is on them, and the keys scroll the page.
    const pageScrolls = () => document.documentElement.scrollHeight > window.innerHeight + 4;
    window.addEventListener('wheel', (event) => {
      if (!media || event.ctrlKey || document.body.classList.contains('bag-open')) return;
      if (pageScrolls() && !event.target.closest?.('.product__media')) return;
      event.preventDefault();
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1);
      const now = performance.now();
      // A gesture ends when the wheel has been quiet for a moment (trackpads coast for a while).
      clearTimeout(quietTimer);
      quietTimer = setTimeout(() => { locked = false; sum = 0; }, 150);
      if (locked) {
        // A fresh, firm push while still coasting (e.g. the next wheel notch) counts as a new gesture.
        if (now - lastTrigger > 320 && Math.abs(delta) >= 60) { lastTrigger = now; moveTo(target + Math.sign(delta)); }
        return;
      }
      sum += delta;
      if (Math.abs(sum) >= NUDGE) { locked = true; lastTrigger = now; moveTo(target + Math.sign(sum)); sum = 0; }
    }, { passive: false });
    window.addEventListener('keydown', (event) => {
      if (!media || event.target.closest?.('input, textarea, select')) return;
      if (pageScrolls() && !['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
      if (['ArrowDown', 'ArrowRight', 'PageDown', ' '].includes(event.key)) { event.preventDefault(); moveTo(target + 1); }
      if (['ArrowUp', 'ArrowLeft', 'PageUp'].includes(event.key)) { event.preventDefault(); moveTo(target - 1); }
    });
    // Touch (tablets in the wide layout): a swipe on the photo, either way, turns to the next / previous one.
    let touchStart = null;
    window.addEventListener('touchstart', (event) => {
      touchStart = media && event.touches.length === 1 && event.target.closest?.('.product__media')
        ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    window.addEventListener('touchend', (event) => {
      if (!media || !touchStart) return;
      const t = event.changedTouches[0];
      const dx = t.clientX - touchStart.x, dy = t.clientY - touchStart.y;
      touchStart = null;
      const d = Math.abs(dx) > Math.abs(dy) ? dx : dy;
      if (Math.abs(d) >= 30) moveTo(target + (d < 0 ? 1 : -1));
    }, { passive: true });
    window.addEventListener('resize', update);
  }

  function update() {
    cancelAnimationFrame(frame); frame = null; clearTimeout(quietTimer);
    pos = from = target = 0; sum = 0; locked = false;
    images.forEach((img) => { img.style.cssText = ''; });
    document.querySelector('.product__counter')?.remove();
    media?.classList.remove('is-stack');
    media = document.querySelector('.product__media');
    images = media ? [...media.querySelectorAll('img')] : [];
    if (!media || images.length < 2 || !enabled()) { media = null; return; }
    media.classList.add('is-stack');
    ahead(images);
    render();
  }

  return { bind, update };
})();

/* Water surface: a ripple height-map drawn on a canvas behind the page.
   The pointer drops ripples along its path; the loop sleeps when the water is calm. */
function initWater() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Phones and touch screens get a lighter pool — bigger cells, so each frame costs a fraction
  // of the desktop one — but the same behaviour: the finger leaves a wake, taps leave a ring,
  // and the pieces lap at the water now and then.
  const LITE = window.matchMedia('(hover: none), (max-width: 749px)').matches;
  const SCALE = LITE ? 8 : 5;   // one simulation cell = this many screen px (upscaled smoothly by the browser)
  const BLEED = 0;        // canvas extends this far past each screen edge (see .water in base.css)
  const DAMPING = 0.978;  // how quickly ripples fade
  const STRENGTH = 90;    // height added per drop
  const RADIUS = LITE ? 3 : 5;   // drop radius in cells (soft gaussian, no hard edge)
  const LIGHT = 0.3;      // how strongly slopes are shaded
  // White water, matching --bg (#ffffff) in base.css: with no brighter white to catch, the
  // ripples are drawn by their shaded side alone — troughs dip to a soft grey — and the pool
  // deepens a little toward the edges.
  const SHALLOW = [255, 255, 255];
  const DEEP = [245, 245, 245];
  const GLINT = [255, 255, 255];
  const SHADE = [214, 214, 214];

  const canvas = document.createElement('canvas');
  canvas.className = 'water';
  canvas.setAttribute('aria-hidden', 'true');
  document.body.prepend(canvas);
  const ctx = canvas.getContext('2d');

  // Precomputed gaussian brush so drops are round and smooth.
  const brush = [];
  for (let dy = -RADIUS; dy <= RADIUS; dy++) {
    for (let dx = -RADIUS; dx <= RADIUS; dx++) {
      const d2 = (dx * dx + dy * dy) / (RADIUS * RADIUS);
      if (d2 <= 1) brush.push([dx, dy, Math.exp(-d2 * 3)]);
    }
  }

  let w, h, cur, prev, image, baseR, baseG, baseB, frame = null, lastPoint = null;

  // The still pool: depth darkens toward the edges, plus a few soft patches of shallower /
  // deeper water so it reads as natural rather than a flat gradient. Computed once per size.
  function paintPool() {
    baseR = new Float32Array(w * h);
    baseG = new Float32Array(w * h);
    baseB = new Float32Array(w * h);
    const patches = Array.from({ length: 7 }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      r: (0.15 + Math.random() * 0.25) * Math.max(w, h),
      k: (Math.random() - 0.5) * 0.5,
    }));
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const nx = x / w - 0.5, ny = y / h - 0.45;
        let depth = Math.min(1, Math.hypot(nx * 1.1, ny * 1.4) * 1.35);
        for (const p of patches) {
          const d2 = ((x - p.x) ** 2 + (y - p.y) ** 2) / (p.r * p.r);
          depth += p.k * Math.exp(-d2 * 2);
        }
        depth = Math.max(0, Math.min(1, depth));
        const t = depth * depth * (3 - 2 * depth); // smoothstep
        const i = y * w + x;
        baseR[i] = SHALLOW[0] + (DEEP[0] - SHALLOW[0]) * t;
        baseG[i] = SHALLOW[1] + (DEEP[1] - SHALLOW[1]) * t;
        baseB[i] = SHALLOW[2] + (DEEP[2] - SHALLOW[2]) * t;
      }
    }
  }

  function resize() {
    // A hidden or zero-sized viewport (a background tab, a collapsed pane) has no area to
    // simulate; wait for the next resize instead of throwing and halting the other init work.
    if (!window.innerWidth || !window.innerHeight) { w = h = 0; image = null; return; }
    w = Math.ceil((window.innerWidth + BLEED * 2) / SCALE);
    h = Math.ceil((window.innerHeight + BLEED * 2) / SCALE);
    canvas.width = w;
    canvas.height = h;
    cur = new Float32Array(w * h);
    prev = new Float32Array(w * h);
    image = ctx.createImageData(w, h);
    paintPool();
    for (let i = 0; i < w * h; i++) {
      image.data[i * 4] = baseR[i]; image.data[i * 4 + 1] = baseG[i]; image.data[i * 4 + 2] = baseB[i]; image.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(image, 0, 0);
  }

  function drop(x, y, force = 1) {
    if (!image) return;
    const cx = Math.round((x + BLEED) * (w / (window.innerWidth + BLEED * 2)));
    const cy = Math.round((y + BLEED) * (h / (window.innerHeight + BLEED * 2)));
    for (const [dx, dy, weight] of brush) {
      const px = cx + dx, py = cy + dy;
      if (px < 1 || py < 1 || px >= w - 1 || py >= h - 1) continue;
      cur[py * w + px] += STRENGTH * weight * force;
    }
  }

  function tick() {
    if (!image) { frame = null; return; }
    let energy = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        // 8-neighbour average (edges weighted over corners) keeps waves round and free of pixel noise.
        const edges = cur[i - 1] + cur[i + 1] + cur[i - w] + cur[i + w];
        const corners = cur[i - w - 1] + cur[i - w + 1] + cur[i + w - 1] + cur[i + w + 1];
        const v = ((edges * 2 + corners) / 6 - prev[i]) * DAMPING;
        prev[i] = v;
        energy += Math.abs(v);
      }
    }
    [cur, prev] = [prev, cur];

    const d = image.data;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        const slope = cur[i - 1] - cur[i + 1] + cur[i - w] - cur[i + w];
        // Soft-clamped light: >0 lifts toward a sky glint, <0 sinks toward green shade.
        const s = Math.tanh((slope * LIGHT) / 40);
        const toward = s > 0 ? GLINT : SHADE;
        const a = Math.abs(s) * (s > 0 ? 0.5 : 0.28);
        const o = i * 4;
        d[o] = baseR[i] + (toward[0] - baseR[i]) * a;
        d[o + 1] = baseG[i] + (toward[1] - baseG[i]) * a;
        d[o + 2] = baseB[i] + (toward[2] - baseB[i]) * a;
      }
    }
    ctx.putImageData(image, 0, 0);

    frame = energy > 0.5 ? requestAnimationFrame(tick) : null;
  }

  function wake() {
    if (!frame) frame = requestAnimationFrame(tick);
  }

  // A tap leaves one ring where the finger lands.
  window.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch') return;
    drop(event.clientX, event.clientY, 1.2);
    lastPoint = { x: event.clientX, y: event.clientY };   // the wake starts here, not where the last touch ended
    wake();
  }, { passive: true });
  ['pointerup', 'pointercancel'].forEach((type) => window.addEventListener(type, (event) => {
    if (event.pointerType === 'touch') lastPoint = null;
  }, { passive: true }));
  let lastTouch = 0;
  window.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'touch') {
      // A finger moves in bursts of events: a drop every 50 ms is enough for an unbroken wake.
      if (event.timeStamp - lastTouch < 50) return;
      lastTouch = event.timeStamp;
    }
    const point = { x: event.clientX, y: event.clientY };
    // Fill the gap between pointer events so fast strokes leave an unbroken wake.
    if (lastPoint) {
      const dist = Math.hypot(point.x - lastPoint.x, point.y - lastPoint.y);
      const steps = Math.min(Math.floor(dist / (SCALE * 2)), 30);
      for (let s = 1; s <= steps; s++) {
        drop(lastPoint.x + ((point.x - lastPoint.x) * s) / (steps + 1), lastPoint.y + ((point.y - lastPoint.y) * s) / (steps + 1));
      }
    }
    drop(point.x, point.y);
    lastPoint = point;
    wake();
  }, { passive: true });
  document.addEventListener('pointerleave', () => { lastPoint = null; });

  // Let the rest of the site drop a ripple (the mini bag sets its pieces down on the water).
  window.MOT.ripple = (x, y, force = 1) => { drop(x, y, force); wake(); };

  // Floating photos lap at the water now and then: a faint ring at the foot of one piece.
  setInterval(() => {
    if (document.hidden) return;
    const pieces = [...document.querySelectorAll('.scatter .item:not([hidden]) .item__media')]
      .map((media) => media.getBoundingClientRect())
      .filter((rect) => rect.bottom > 0 && rect.top < window.innerHeight);
    if (!pieces.length) return;
    const rect = pieces[Math.floor(Math.random() * pieces.length)];
    drop(rect.left + rect.width * (0.3 + Math.random() * 0.4), rect.bottom - 4, 0.35);
    wake();
  }, 2400);

  let resizeTimer, sizedW = window.innerWidth, sizedH = window.innerHeight;
  window.addEventListener('resize', () => {
    // A phone's address bar sliding away changes the height on every scroll: the pool is
    // only rebuilt when the width changes or the height changes by a real amount.
    if (LITE && window.innerWidth === sizedW && Math.abs(window.innerHeight - sizedH) < 160) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { sizedW = window.innerWidth; sizedH = window.innerHeight; resize(); }, 150);
  });

  resize();
}

/* Add to bag without leaving the page. */
/* Bag page: one large photo beside a typeset list of the pieces (base.css, Bag page).
   Less / More / Remove change the bag in place — only the numbers that changed
   are rewritten, so nothing on the page blinks. A removed piece fades out and the others
   close up. Works on the preview's sample bag and on Shopify (/cart/change.js). */
function initCartPage() {
  const form = document.querySelector('.cart__form');
  if (!form || !window.MOT.cart) return;
  ahead([...form.querySelectorAll('.cart__media img')]);
  let busy = false;

  // More is struck out, and cannot be pressed, on a piece the bag already holds every copy of.
  const markMax = (line) => {
    const more = line.querySelector('[data-qty-step="1"]');
    if (!more) return;
    const full = Number(line.dataset.quantity) >= stockOf(parseInt(line.dataset.key, 10));
    more.classList.toggle('is-max', full);
    more.disabled = full;
    if (full) { more.setAttribute('aria-disabled', 'true'); more.title = 'No more in stock'; } else { more.removeAttribute('aria-disabled'); more.removeAttribute('title'); }
  };
  form.querySelectorAll('.cart__line[data-key]').forEach(markMax);

  async function change(line, qty) {
    if (busy) return;
    busy = true;
    const provider = window.MOT.cart();
    try {
      await provider.set(line.dataset.key, qty);
      const lines = await provider.lines();
      const current = lines.find((l) => String(l.handle) === line.dataset.key);
      if (current) {
        line.dataset.quantity = current.qty;
        markMax(line);
        line.querySelector('[data-qty]').textContent = current.qty;
        const linePrice = line.querySelector('[data-line-price]');
        if (linePrice.dataset.cents) linePrice.dataset.cents = current.line_price;
        linePrice.textContent = provider.format(current.line_price);
        const input = line.querySelector('[data-qty-input]');
        if (input) input.value = current.qty;
      } else {
        if (line.classList.contains('is-active')) activate(line.nextElementSibling || line.previousElementSibling);
        line.classList.add('is-removing');
        await new Promise((r) => setTimeout(r, 450));
        line.remove();
        form.querySelector(`.cart__media img[data-key="${CSS.escape(line.dataset.key)}"]`)?.remove();
      }
      const pieces = lines.reduce((n, l) => n + l.qty, 0);
      const label = form.querySelector('[data-pieces]');
      if (label) label.textContent = `${pieces} ${pieces === 1 ? 'piece' : 'pieces'}`;
      const subtotal = form.querySelector('[data-subtotal]');
      if (subtotal) {
        const total = await provider.subtotal();
        if (subtotal.dataset.cents) subtotal.dataset.cents = total;
        subtotal.textContent = provider.format(total);
      }
      window.MOT.setBagCount(pieces);
      if (!lines.length) {
        // The last piece is gone: load the page's empty state (pieces to pick up).
        busy = false;
        if (window.MOT.go) window.MOT.go(location.href, false); else location.reload();
        return;
      }
    } catch (error) {
      location.reload();   // anything odd: show the true state of the bag
    }
    busy = false;
  }

  // The row under the pointer (or with focus) takes the dot, and its photo opens in the
  // frame: mask from the bottom edge, settling from a zoom, over the photo that was there.
  function activate(line) {
    if (!line?.dataset?.key || line.classList.contains('is-active')) return;
    form.querySelectorAll('.cart__line.is-active').forEach((el) => el.classList.remove('is-active'));
    line.classList.add('is-active');
    const next = form.querySelector(`.cart__media img[data-key="${CSS.escape(line.dataset.key)}"]`);
    if (!next) return;
    form.querySelectorAll('.cart__media img').forEach((img) => {
      if (img === next) return;
      img.classList.toggle('is-prev', img.classList.contains('is-active'));
      img.classList.remove('is-active');
    });
    next.classList.remove('is-prev');
    void next.offsetHeight;   // start from the closed mask, then open
    next.classList.add('is-active');
  }
  const over = (event) => activate(event.target.closest?.('.cart__line[data-key]'));
  form.addEventListener('pointerover', over);
  form.addEventListener('focusin', over);

  // Names stay on one line; only when one is wider than its whole column do they wrap.
  const list = form.querySelector('.cart__lines');
  function fitNames() {
    const names = [...form.querySelectorAll('.cart__name')];
    if (!names.length) return;
    const textWidth = (el) => { const r = document.createRange(); r.selectNodeContents(el); return r.getBoundingClientRect().width; };
    list.classList.remove('is-wrapping');
    const widest = Math.max(...names.map((name) => textWidth(name.children[1])));
    const room = names[0].getBoundingClientRect().width - names[0].children[0].getBoundingClientRect().width - 4;
    list.classList.toggle('is-wrapping', widest > room);
  }
  fitNames();
  new MutationObserver(fitNames).observe(list, { childList: true });
  window.addEventListener('resize', () => { if (list.isConnected) fitNames(); });
  document.fonts?.ready.then(() => { if (list.isConnected) fitNames(); });

  form.addEventListener('click', (event) => {
    const line = event.target.closest('.cart__line[data-key]');
    if (!line) return;
    const step = event.target.closest('[data-qty-step]');
    const remove = event.target.closest('[data-remove]');
    if (!step && !remove) return;
    event.preventDefault();
    change(line, remove ? 0 : Math.max(0, Number(line.dataset.quantity) + Number(step.dataset.qtyStep)));
  });
}

/* (+) under a piece on the empty bag page: adds it straight to the bag, with a ripple
   where it was picked up, then the page shows the bag with the piece in it. */
function initQuickAdd() {
  // Bound once on the document: the empty bag's pieces are drawn after the page arrives
  // (and again whenever the bag empties), so there is nothing to bind to at page set-up.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('.cart__picks [data-add]');
    if (!button || !window.MOT.cart || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    button.setAttribute('aria-busy', 'true');
    const r = button.closest('li').getBoundingClientRect();
    window.MOT.ripple?.(r.left + r.width / 2, r.top + r.height / 2, 1.6);
    try {
      await window.MOT.cart().add(button.dataset.add);
      window.MOT.bag?.refresh();
      if (window.MOT.go) window.MOT.go(location.href, false); else location.reload();
    } catch (error) {
      button.removeAttribute('aria-busy');
    }
  });
}

/* The theme's dropdown (e.g. Subject on the contact form): a field that shows the current
   choice and opens a panel of ( ) options. The options are real radio buttons, so arrow
   keys move the choice and the form submits it; choosing closes the panel, as do Escape
   and a press anywhere else. */
function initDropdowns() {
  document.querySelectorAll('[data-dropdown]').forEach((root) => {
    const toggle = root.querySelector('.dropdown__toggle');
    const panel = root.querySelector('.dropdown__panel');
    const current = root.querySelector('[data-current]');
    if (!toggle || !panel) return;
    const radios = () => [...panel.querySelectorAll('input[type="radio"]')];
    const label = (input) => input.closest('label').lastElementChild.textContent.trim();   // the words, not the ( ) mark
    const sync = () => { const chosen = radios().find((r) => r.checked); if (chosen && current) current.textContent = label(chosen); };
    const open = () => {
      root.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      (radios().find((r) => r.checked) || radios()[0])?.focus({ preventScroll: true });
    };
    const close = (refocus) => {
      if (!root.classList.contains('is-open')) return;
      root.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      if (refocus) toggle.focus({ preventScroll: true });
    };
    sync();
    toggle.addEventListener('click', () => (root.classList.contains('is-open') ? close(true) : open()));
    panel.addEventListener('change', sync);
    // A press on an option chooses and closes; arrow keys only move the choice.
    panel.addEventListener('click', (event) => { if (event.detail > 0 && event.target.closest('.choice__option')) setTimeout(() => close(true), 0); });
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.stopPropagation(); close(true); }
      if (event.key === 'Enter' && event.target.matches('input[type="radio"]')) { event.preventDefault(); close(true); }
    });
    document.addEventListener('pointerdown', (event) => { if (root.isConnected && !root.contains(event.target)) close(false); });
    root.addEventListener('focusout', (event) => { if (event.relatedTarget && !root.contains(event.relatedTarget)) close(false); });
  });
}

/* FAQ: one topic at a time. The topics are a list of ( ) choices; pressing one (or moving
   to it with the arrow keys) shows that topic's questions and answers. */
function initFaq() {
  const root = document.querySelector('[data-faq]');
  if (!root) return;
  const tabs = [...root.querySelectorAll('.faq__tab')];
  const panels = [...root.querySelectorAll('.faq__panel')];
  if (!tabs.length) return;
  // Phones: a row of ( ) topics right under the header reads as a second menu, so there the
  // topics sit in one field (the theme's dropdown) that shows the current one — base.css
  // shows the field or the list, never both.
  const name = (tab) => tab.lastElementChild.textContent.trim();
  const field = document.createElement('div');
  field.className = 'faq__select';
  field.innerHTML = `
    <span class="faq__select-label" id="faq-topic-label">Topic</span>
    <div class="dropdown" data-dropdown>
      <button type="button" class="dropdown__toggle" aria-expanded="false" aria-labelledby="faq-topic-label faq-topic-current"><span id="faq-topic-current" data-current></span><svg class="dropdown__chevron" viewBox="0 0 12 12" aria-hidden="true"><path d="M1.5 4l4.5 4.5L10.5 4" fill="none" stroke="currentColor" stroke-width="1" vector-effect="non-scaling-stroke"/></svg></button>
      <div class="dropdown__panel" role="radiogroup" aria-labelledby="faq-topic-label">${tabs.map((tab, i) => `
        <label class="choice__option" style="--i:${i}"><input type="radio" name="faq-topic" value="${i}"><span class="filter__mark" aria-hidden="true">(<span class="filter__dot">●</span>)</span><span>${name(tab)}</span></label>`).join('')}
      </div>
    </div>`;
  tabs[0].closest('.faq__tabs').after(field);
  const radios = [...field.querySelectorAll('input')];

  const select = (tab, focus) => {
    tabs.forEach((t) => { const on = t === tab; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; });
    panels.forEach((panel) => panel.classList.toggle('is-active', panel.id === tab.getAttribute('aria-controls')));
    radios[tabs.indexOf(tab)].checked = true;
    field.querySelector('[data-current]').textContent = name(tab);
    if (focus) tab.focus({ preventScroll: true });
  };
  field.addEventListener('change', (event) => { const tab = tabs[Number(event.target.value)]; if (tab) select(tab, false); });
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => select(tab, false));
    tab.addEventListener('keydown', (event) => {
      const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      select(tabs[(index + step + tabs.length) % tabs.length], true);
    });
  });
  select(tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0], false);
}

function initProductForm() {
  const form = document.querySelector('[data-product-form]');
  if (!form) return;

  const select = form.querySelector('select[name="id"]');
  const price = document.querySelector('[data-price]');
  select?.addEventListener('change', () => {
    const option = select.selectedOptions[0];
    if (price && option.dataset.cents && window.MOT.shopCurrency) { price.dataset.cents = option.dataset.cents; price.textContent = money(Number(option.dataset.cents)); }
    else if (price && option.dataset.price) price.textContent = option.dataset.price;
  });

  const button = form.querySelector('[type="submit"]');
  const status = form.querySelector('.product__status');

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    button.disabled = true;
    status.textContent = '';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Could not add to bag');
      // No more than the shop holds: a second copy of a one-off piece is taken back out.
      const over = await trimBag();
      status.textContent = over.length ? (over[0].max === 1 ? 'Only one of this piece exists.' : `Only ${over[0].max} available.`) : '';
      window.MOT.bag?.refresh();
      window.MOT.bag?.open();
    } catch (error) {
      status.textContent = error.message;
    } finally {
      button.disabled = false;
    }
  });
}

// Snail pieces are numbered with the numero sign: "Snail 11" and "Snail No.11" both read
// "Snail №11" (the same rule as snippets/piece-name.liquid). Other names are left as written.
function numero(name) {
  name = String(name);
  if (!/^snail\s/i.test(name)) return name;
  return name.replace(/No\.?\s*(?=\d)/, '№').replace(/^(snail\s+)(?=\d)/i, '$1№');
}


// Stock (layout/theme.liquid writes window.MOT_STOCK: variant id → how many the shop holds).
// A piece that is not listed there is not counted, so it has no limit.
function stockOf(variantId) {
  const n = window.MOT_STOCK && window.MOT_STOCK[variantId];
  return typeof n === 'number' ? n : Infinity;
}
// Brings every line of the bag back within stock; returns the lines it had to lower.
async function trimBag() {
  if (!window.MOT_STOCK) return [];
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
  const over = (cart.items || []).filter((item) => item.quantity > stockOf(item.variant_id));
  for (const item of over) {
    await fetch('/cart/change.js', { method: 'POST', headers: json, body: JSON.stringify({ id: item.key, quantity: stockOf(item.variant_id) }) });
  }
  return over.map((item) => ({ title: item.product_title, max: stockOf(item.variant_id) }));
}

/* ---------- Snail map ----------
   Under the Snail heading, half the page is a globe that can be turned by hand: every snail
   that has left the studio is a dot where it lives now, with its number beside it. The other
   half keeps the snails that are for sale.
   Data: window.MOT.snails — one snail per line, "number | city | country | latitude | longitude"
   (Theme settings → Snail map). The land (a grid of dots and the coastline) is its own small
   file, fetched the first time the map is shown. Wide screens: the map is fixed on the left
   half; phones: it sits above the photos.
   Only snails that have been bought are on the map. Under the snails for sale, the same snails
   are listed — the photo of the snail that customer received, then its number, place, how far
   that is from the studio, and who adopted it; a press on one turns the globe to it. A line
   may add "| adopted by | photo link" after the longitude. */
function initSnailMap() {
  if (window.MOT.snailMap) return;
  const places = [], snails = [];
  String(window.MOT.snails || '').split('\n').forEach((line) => {
    const [no, city, country, lat, lng, by, photoOut, photoIn] = line.split('|').map((part) => part.trim());
    if (!no || !isFinite(parseFloat(lat)) || !isFinite(parseFloat(lng))) return;
    snails.push({ no, city, country, lat: parseFloat(lat), lng: parseFloat(lng), by, photoOut, photoIn });
    const key = `${parseFloat(lat).toFixed(2)},${parseFloat(lng).toFixed(2)}`;
    let place = places.find((p) => p.key === key);
    if (!place) places.push(place = { key, city, country, lat: parseFloat(lat), lng: parseFloat(lng), nos: [] });
    place.nos.push(no);
  });
  // No list (the map is switched off in the theme settings, or the list is empty): Snail is a
  // shop page like the other headings.
  if (!places.length || !window.MOT.snailLand) { window.MOT.snailMap = () => {}; window.MOT.snailMapOn = false; return; }
  window.MOT.snailMapOn = true;
  const count = places.reduce((n, p) => n + p.nos.length, 0);
  const summary = `${count} ${count === 1 ? 'snail' : 'snails'}, ${places.length} ${places.length === 1 ? 'place' : 'places'}`;

  const root = document.createElement('section');
  root.className = 'snailmap';
  root.hidden = true;
  root.setAttribute('aria-label', 'Snail map');
  root.innerHTML = `
    <canvas class="snailmap__globe" role="img" aria-label="A globe showing where each snail lives now: ${summary}."></canvas>
    <p class="snailmap__label visually-hidden" aria-live="polite"></p>`;   // read out, not shown: the place is written beside the dot itself
  const canvas = root.querySelector('canvas'), ctx = canvas.getContext('2d');
  const label = root.querySelector('.snailmap__label');
  const RAD = Math.PI / 180;
  const unit = (lng, lat) => [Math.cos(lat * RAD) * Math.sin(lng * RAD), Math.sin(lat * RAD), Math.cos(lat * RAD) * Math.cos(lng * RAD)];
  places.forEach((p) => { p.v = unit(p.lng, p.lat); });
  // Where every snail starts: Mot, in the south of Thailand. Not a pin — a dotted circle about
  // 110 km across the land around the studio, with its name under it.
  const MOT_HOME = { lat: 7.62, lng: 100.08, km: 110, name: 'Mot, somewhere in southern Thailand' };
  const homeRing = (() => {
    const c = unit(MOT_HOME.lng, MOT_HOME.lat), rho = MOT_HOME.km / 6371;
    const u = [Math.cos(MOT_HOME.lng * RAD), 0, -Math.sin(MOT_HOME.lng * RAD)];            // east
    const v = [c[1] * u[2] - c[2] * u[1], c[2] * u[0] - c[0] * u[2], c[0] * u[1] - c[1] * u[0]];   // north
    return Array.from({ length: 64 }, (_, i) => {
      const t = (i / 64) * Math.PI * 2, a = Math.cos(t) * Math.sin(rho), b = Math.sin(t) * Math.sin(rho), k = Math.cos(rho);
      return [c[0] * k + u[0] * a + v[0] * b, c[1] * k + u[1] * a + v[1] * b, c[2] * k + u[2] * a + v[2] * b];
    });
  })();

  // The view: which point of the earth faces us (lng0, lat0) and how close we are (zoom 1 = whole globe).
  const home = { lng: places.reduce((s, p) => s + p.lng, 0) / places.length, lat: places.reduce((s, p) => s + p.lat, 0) / places.length };
  const view = { lng: home.lng, lat: home.lat, zoom: 1 };
  const MIN = 1, MAX = 40;
  let land = null, coast = null, size = { w: 0, h: 0, dpr: 1 }, clusters = [], chosen = null, frame = 0, on = false;
  let lit = true, blink = 0;         // the chosen place's circle blinks (lit / not), about once a second
  let look = null, moving = false;   // look: colours and type, read once; moving: the globe is being turned right now

  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const blinking = () => {
    clearInterval(blink); blink = 0; lit = true;
    if (chosen && on && !still.matches) blink = setInterval(() => { lit = !lit; if (!frame) frame = requestAnimationFrame(draw); }, 520);
  };
  const say = () => {
    blinking();
    if (!chosen) { label.textContent = `Snail map: ${summary}.`; return; }
    label.textContent = chosen.places.map((p) => `№${p.nos.join(' · ')} — ${[p.city, p.country].filter(Boolean).join(', ')}`).join('  /  ');
  };

  function draw() {
    frame = 0;
    const { w, h, dpr } = size;
    if (!w || !h) return;
    // Kept light for phones: colours and type are read once (look), no object is made per point,
    // and the land's dots are filled in three strokes of the brush instead of one per dot.
    if (!look) look = { ink: getComputedStyle(root).color, paper: getComputedStyle(document.documentElement).getPropertyValue('--bg').trim() || '#fff', font: `${getComputedStyle(root).fontSize} ${getComputedStyle(root).fontFamily}`, lh: parseFloat(getComputedStyle(root).fontSize) * 1.3 };
    const { ink, paper } = look;
    const R = Math.min(w, h) * 0.46 * view.zoom, cx = w / 2, cy = h / 2;
    const sinL = Math.sin(view.lng * RAD), cosL = Math.cos(view.lng * RAD), sinP = Math.sin(view.lat * RAD), cosP = Math.cos(view.lat * RAD);
    // Turn a point of the unit sphere to the view; z > 0 is the side facing us.
    const turn = (x, y, z) => {
      const x1 = x * cosL - z * sinL, z1 = x * sinL + z * cosL;
      return [x1, y * cosP - z1 * sinP, y * sinP + z1 * cosP];
    };
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = ink; ctx.strokeStyle = ink;
    // The sphere's edge, when it is in the frame
    ctx.globalAlpha = 0.14; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();
    if (land) {
      // Land as a grid of dots; they thin out toward the edge like print on a ball
      const r = Math.max(0.7, Math.min(2.2, R * 0.0042)), TAU = Math.PI * 2;
      const tones = [new Path2D(), new Path2D(), new Path2D()];
      for (let i = 0; i < land.length; i += 3) {
        const x0 = land[i], y0 = land[i + 1], z0 = land[i + 2];
        const z1 = x0 * sinL + z0 * cosL, z = y0 * sinP + z1 * cosP;
        if (z <= 0.02) continue;
        const px = cx + (x0 * cosL - z0 * sinL) * R, py = cy - (y0 * cosP - z1 * sinP) * R;
        if (px < -4 || py < -4 || px > w + 4 || py > h + 4) continue;
        const tone = tones[z < 0.35 ? 0 : z < 0.7 ? 1 : 2];
        tone.moveTo(px + r, py); tone.arc(px, py, r, 0, TAU);
      }
      [0.14, 0.21, 0.28].forEach((alpha, k) => { ctx.globalAlpha = alpha; ctx.fill(tones[k]); });
      // The coastline, drawn finer the closer we are. While the globe is being turned from far
      // away, every other point is enough.
      ctx.globalAlpha = Math.min(0.42, 0.14 + view.zoom * 0.03); ctx.lineWidth = 0.8; ctx.lineJoin = 'round';
      const skip = moving && view.zoom < 2.5 ? 6 : 3;
      ctx.beginPath();
      for (const ring of coast) {
        let pen = false;
        for (let i = 0; i < ring.length; i += skip) {
          const x0 = ring[i], y0 = ring[i + 1], z0 = ring[i + 2];
          const z1 = x0 * sinL + z0 * cosL;
          if (y0 * sinP + z1 * cosP <= 0) { pen = false; continue; }
          const px = cx + (x0 * cosL - z0 * sinL) * R, py = cy - (y0 * cosP - z1 * sinP) * R;
          if (pen) ctx.lineTo(px, py); else ctx.moveTo(px, py);
          pen = true;
        }
      }
      ctx.stroke();
    }
    // Mot's own circle, dotted, under the snails' discs. Its name appears once we are close enough
    // for the circle to be a circle; from far away it is a small ring of dots in the south of Thailand.
    {
      const [hx, hy, hz] = turn(...unit(MOT_HOME.lng, MOT_HOME.lat));
      if (hz > 0.05) {
        const radius = (MOT_HOME.km / 6371) * R, step = radius < 14 ? 4 : radius < 40 ? 2 : 1;
        ctx.globalAlpha = 0.75;
        ctx.beginPath();
        for (let i = 0; i < homeRing.length; i += step) {
          const [x, y, z] = turn(homeRing[i][0], homeRing[i][1], homeRing[i][2]);
          if (z <= 0) continue;
          const px = cx + x * R, py = cy - y * R;
          ctx.moveTo(px + 1.1, py); ctx.arc(px, py, 1.1, 0, Math.PI * 2);
        }
        ctx.fill();
        if (radius >= 22) {
          const tx = cx + hx * R, ty = cy - hy * R + radius + 16;
          ctx.font = look.font; ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
          ctx.save();
          ctx.globalAlpha = 1; ctx.fillStyle = paper; ctx.strokeStyle = paper; ctx.shadowColor = paper; ctx.shadowBlur = 9; ctx.lineWidth = 5; ctx.lineJoin = 'round';
          ctx.strokeText(MOT_HOME.name, tx, ty); ctx.strokeText(MOT_HOME.name, tx, ty);
          ctx.restore();
          ctx.globalAlpha = 1; ctx.fillStyle = ink;
          ctx.fillText(MOT_HOME.name, tx, ty);
          ctx.textAlign = 'left';
        }
        ctx.strokeStyle = ink; ctx.fillStyle = ink;
      }
    }
    // The snails: every place is a white disc with how many snails live there, and its name
    // beside it — always, from far or near. Places too close to stand side by side at this
    // distance are moved down into a column, in order from north to south, never overprinted.
    clusters = [];
    places.forEach((p) => {
      const [x, y, z] = turn(p.v[0], p.v[1], p.v[2]);
      if (z <= 0.05) return;
      clusters.push({ x: cx + x * R, y: cy - y * R, places: [p] });
    });
    const GAP = 28;
    for (let pass = 0, moved = true; moved && pass < 12; pass++) {
      moved = false;
      clusters.sort((a, b) => a.y - b.y);
      for (let i = 1; i < clusters.length; i++) {
        for (let k = 0; k < i; k++) {
          if (Math.abs(clusters[k].x - clusters[i].x) < 170 && Math.abs(clusters[i].y - clusters[k].y) < GAP - 0.5) { clusters[i].y = clusters[k].y + GAP; moved = true; }
        }
      }
    }
    // A dotted line from Mot's circle to every place a snail went — the way it travelled, drawn
    // along the curve of the globe. It leaves from the edge of Mot's circle and stops at the
    // place's disc (also when the disc has been moved aside to make room).
    {
      const m = unit(MOT_HOME.lng, MOT_HOME.lat), [mx, my, mz] = turn(m[0], m[1], m[2]);
      if (mz > 0) {
        ctx.save();
        ctx.globalAlpha = 0.7; ctx.lineWidth = 1.6; ctx.lineCap = 'round'; ctx.setLineDash([0.1, 5]);
        ctx.beginPath();
        clusters.forEach((c) => {
          const v = c.places[0].v;
          const dot = Math.max(-1, Math.min(1, m[0] * v[0] + m[1] * v[1] + m[2] * v[2])), angle = Math.acos(dot);
          if (angle < 1e-4) return;
          // Start exactly on the edge of Mot's circle (a place inside the circle gets no line)
          // and always take enough steps for a line, however near the place is.
          const t0 = (MOT_HOME.km / 6371) / angle;
          if (t0 >= 1) return;
          const steps = Math.max(8, Math.ceil((angle * (1 - t0)) / 0.01)), sin = Math.sin(angle);
          let pen = false;
          for (let i = 0; i <= steps; i++) {
            const t = t0 + (1 - t0) * (i / steps), a = Math.sin((1 - t) * angle) / sin, b = Math.sin(t * angle) / sin;
            const [x, y, z] = turn(m[0] * a + v[0] * b, m[1] * a + v[1] * b, m[2] * a + v[2] * b);
            if (z <= 0) { pen = false; continue; }
            let px = cx + x * R, py = cy - y * R;
            if (i === steps) { px = c.x; py = c.y; }                       // end on the disc itself
            if (pen) ctx.lineTo(px, py); else ctx.moveTo(px, py);
            pen = true;
          }
        });
        ctx.stroke();
        ctx.restore();
      }
    }
    ctx.font = look.font;
    ctx.textBaseline = 'middle';
    clusters.forEach((c) => {
      const p = c.places[0], picked = chosen && chosen.places.includes(p), ring = picked ? 14 : 12;
      // The disc stands on the page colour, so no coastline runs through it.
      ctx.globalAlpha = 1; ctx.fillStyle = paper;
      ctx.beginPath(); ctx.arc(c.x, c.y, ring, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = ink;
      ctx.textAlign = 'center';
      ctx.fillText(String(p.nos.length), c.x, c.y + 1);
      ctx.textAlign = 'left';
      // The place name stands on a soft cloud of the page colour, so the coastline and the dots
      // of the land behind it do not cut through the letters.
      const name = p.city || p.country || '', nx = c.x + ring + 7, ny = c.y + 1;
      ctx.save();
      ctx.fillStyle = paper; ctx.strokeStyle = paper; ctx.shadowColor = paper; ctx.shadowBlur = 9;
      ctx.lineWidth = 5; ctx.lineJoin = 'round';
      ctx.strokeText(name, nx, ny); ctx.strokeText(name, nx, ny); ctx.fillText(name, nx, ny);
      ctx.restore();
      ctx.fillText(name, nx, ny);
      // The chosen place blinks: its circle goes from full black to almost nothing and back.
      ctx.globalAlpha = picked ? (lit ? 1 : 0.12) : 0.25; ctx.lineWidth = picked ? 1.5 : 1;
      ctx.beginPath(); ctx.arc(c.x, c.y, ring, 0, Math.PI * 2); ctx.stroke();
    });
    ctx.globalAlpha = 1;
  }
  const paint = () => { if (on && !frame) frame = requestAnimationFrame(draw); };

  // Glide the view to a place (press on a dot, Closer / Further, the first arrival)
  let glide = 0;
  function fly(to, ms = 900) {
    cancelAnimationFrame(glide);
    const from = { ...view }, start = performance.now();
    const dl = ((to.lng ?? from.lng) - from.lng + 540) % 360 - 180;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const end = { lng: from.lng + dl, lat: to.lat ?? from.lat, zoom: Math.max(MIN, Math.min(MAX, to.zoom ?? from.zoom)) };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.hidden) { Object.assign(view, end); draw(); return; }
    const step = (now) => {
      const t = Math.min(1, (now - start) / ms), k = ease(t);
      view.lng = from.lng + dl * k; view.lat = from.lat + (end.lat - from.lat) * k;
      view.zoom = from.zoom * Math.pow(end.zoom / from.zoom, k);
      draw();
      if (t < 1) glide = requestAnimationFrame(step);
    };
    glide = requestAnimationFrame(step);
  }

  // Turning by hand: the point under the finger follows it; a flick keeps turning and slows down.
  let drag = null, spin = 0, moved = 0;
  const pinch = new Map();
  const degPerPx = () => 180 / (Math.PI * Math.min(size.w, size.h) * 0.46 * view.zoom);
  canvas.addEventListener('pointerdown', (event) => {
    try { canvas.setPointerCapture(event.pointerId); } catch (e) {}
    pinch.set(event.pointerId, { x: event.clientX, y: event.clientY });
    cancelAnimationFrame(glide); cancelAnimationFrame(spin);
    drag = { x: event.clientX, y: event.clientY, vx: 0, vy: 0, t: performance.now() };
    moved = 0;
    moving = true;
    canvas.classList.add('is-held');
  });
  canvas.addEventListener('pointermove', (event) => {
    if (!pinch.has(event.pointerId)) return;
    if (pinch.size === 2) {   // two fingers: closer / further
      const [a, b] = [...pinch.values()], before = Math.hypot(a.x - b.x, a.y - b.y);
      pinch.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const [c, d] = [...pinch.values()], after = Math.hypot(c.x - d.x, c.y - d.y);
      if (before > 0) view.zoom = Math.max(MIN, Math.min(MAX, view.zoom * after / before));
      moved = 99; drag = null; paint();
      return;
    }
    pinch.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (!drag) return;
    const dx = event.clientX - drag.x, dy = event.clientY - drag.y, now = performance.now(), k = degPerPx();
    moved += Math.abs(dx) + Math.abs(dy);
    view.lng -= dx * k; view.lat = Math.max(-85, Math.min(85, view.lat + dy * k));
    const dt = Math.max(1, now - drag.t);
    drag = { x: event.clientX, y: event.clientY, vx: dx / dt, vy: dy / dt, t: now };
    paint();
  });
  const release = (event) => {
    pinch.delete(event.pointerId);
    canvas.classList.remove('is-held');
    if (!drag) return;
    const held = drag; drag = null;
    if (moved < 6) { moving = false; press(event); return; }
    // A flick keeps the globe turning, but never so fast that the snails are thrown out of sight.
    const LIMIT = 0.9, speed = Math.hypot(held.vx, held.vy), ease = speed > LIMIT ? LIMIT / speed : 1;
    let vx = held.vx * ease, vy = held.vy * ease, last = performance.now();
    const coast = (now) => {
      const dt = Math.min(40, now - last); last = now;
      const k = degPerPx();
      view.lng -= vx * dt * k; view.lat = Math.max(-85, Math.min(85, view.lat + vy * dt * k));
      vx *= Math.pow(0.993, dt); vy *= Math.pow(0.993, dt);
      draw();
      if (Math.abs(vx) + Math.abs(vy) > 0.01) spin = requestAnimationFrame(coast); else { moving = false; draw(); }
    };
    if (Math.abs(vx) + Math.abs(vy) > 0.05) spin = requestAnimationFrame(coast); else { moving = false; draw(); }
  };
  canvas.addEventListener('pointerup', release);
  canvas.addEventListener('pointercancel', release);
  // A press on a dot names its snails and comes closer, until places that share a dot come apart.
  function press(event) {
    const box = canvas.getBoundingClientRect(), x = event.clientX - box.left, y = event.clientY - box.top;
    const hit = clusters.map((c) => ({ c, d: Math.hypot(c.x - x, c.y - y) })).filter((h) => h.d < 28).sort((a, b) => a.d - b.d)[0];
    chosen = hit ? { places: hit.c.places } : null;
    say();
    if (!hit) { draw(); return; }
    const ps = hit.c.places;
    const to = { lng: ps.reduce((s, p) => s + p.lng, 0) / ps.length, lat: ps.reduce((s, p) => s + p.lat, 0) / ps.length };
    fly({ ...to, zoom: Math.max(view.zoom, 4) });
  }
  canvas.addEventListener('wheel', (event) => {
    event.preventDefault();
    view.zoom = Math.max(MIN, Math.min(MAX, view.zoom * Math.exp(-event.deltaY * 0.0022)));
    paint();
  }, { passive: false });

  new ResizeObserver(() => {
    const box = canvas.getBoundingClientRect(), dpr = Math.min(2, window.devicePixelRatio || 1);
    size = { w: box.width, h: box.height, dpr };
    canvas.width = Math.round(box.width * dpr); canvas.height = Math.round(box.height * dpr);
    look = null;
    draw();
  }).observe(canvas);

  let loading = null;
  const load = () => loading || (loading = fetch(window.MOT.snailLand).then((res) => res.json()).then((data) => {
    const pack = (flat) => { const out = new Float32Array((flat.length / 2) * 3); for (let i = 0, j = 0; i < flat.length; i += 2, j += 3) { const v = unit(flat[i], flat[i + 1]); out[j] = v[0]; out[j + 1] = v[1]; out[j + 2] = v[2]; } return out; };
    land = pack(data.land); coast = data.coast.map(pack);
    draw();
  }).catch(() => {}));

  // The snails that have found a home, newest number first; each one is drawn as its own journey. Distances are straight lines
  // from the studio in Phatthalung, southern Thailand; the heading adds them all up.
  const STUDIO = { lat: 7.62, lng: 100.08 };
  const km = (p) => {
    const a = Math.sin((p.lat - STUDIO.lat) * RAD / 2) ** 2 + Math.cos(p.lat * RAD) * Math.cos(STUDIO.lat * RAD) * Math.sin((p.lng - STUDIO.lng) * RAD / 2) ** 2;
    return 6371 * 2 * Math.asin(Math.sqrt(a));
  };
  const total = Math.round(snails.reduce((sum, snail) => sum + km(snail), 0));
  const esc = (text) => String(text || '').replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
  const link = (url) => (/^https:\/\//.test(url || '') ? esc(url) : '');
  const list = document.createElement('section');
  list.className = 'adopted';
  list.setAttribute('aria-label', 'Snails that have found a home');
  // Two photos each, in front of the words: where the snail started (its goodbye photo at the
  // studio) and where it arrived (the photo from its new home; the snail drawing until one comes).
  // They are shown at 34px, so copies 120px wide are asked for — a few kilobytes each instead of
  // the full photo (Google Drive links carry the size as sz=w…).
  // Small copies of the photos are kept in the theme itself (assets/snailmap-<number>-a|b-s.jpg:
  // a at Mot, b in its new home), so they come from the shop's own fast servers.
  // A snail added later in the settings has no copy yet: its photo then comes from its link.
  const assets = String(window.MOT.snailLand || '').replace(/snail-land\.json.*$/, '');
  const kept = (snail, kind, size) => `${assets}snailmap-${encodeURIComponent(snail.no)}-${kind}-${size}.jpg`;
  const sized = (url, width) => url.replace(/([?&])sz=w\d+/, `$1sz=w${width}`);
  // One photo to a row: the one its new owner sent; when none has come, the photo taken at Mot.
  const thumb = (snail) => {
    const mine = link(snail.photoIn), url = mine ? snail.photoIn : snail.photoOut, kind = mine ? 'b' : 'a';
    return link(url)
      ? `<span class="adopted__photo"><img src="${esc(kept(snail, kind, 's'))}" data-fallback="${esc(sized(url, 120))}" width="120" height="120" alt="${esc(mine ? `Snail ${snail.no} in its new home` : `Snail ${snail.no} at Mot`)}" loading="lazy" decoding="async" referrerpolicy="no-referrer"></span>`
      : `<span class="adopted__photo is-empty">${window.MOT.snailMark ? `<img src="${esc(window.MOT.snailMark)}" width="120" height="96" alt="" loading="lazy" decoding="async">` : ''}</span>`;
  };
  const trip = (snail) => `<span class="adopted__trip">
        ${thumb(snail)}
        <span class="adopted__words"><span class="adopted__name">Snail №${esc(snail.no)}</span><span class="adopted__note">From Mot</span></span>
        <span class="adopted__words is-end"><span class="adopted__name">To ${esc([snail.city, snail.country].filter(Boolean).join(', '))}</span><span class="adopted__note">${Math.round(km(snail)).toLocaleString('en-US')} km${snail.by ? ` · with ${esc(snail.by)}` : ''}</span></span>
      </span>`;
  // The heading is its own piece: it stays where it is while only the list scrolls under it —
  // fixed beside the page names on wide screens, held under the globe on phones (base.css).
  const head = document.createElement('p');
  head.className = 'adopted__head';
  head.innerHTML = `(Found a home) <span>${count} ${count === 1 ? 'snail has' : 'snails have'} crawled ${total.toLocaleString('en-US')} km together</span>`;
  new ResizeObserver(() => { list.style.setProperty('--head', `${head.offsetHeight}px`); }).observe(head);
  list.innerHTML = [...snails].sort((a, b) => parseFloat(b.no) - parseFloat(a.no)).map((snail) => `
    <button type="button" class="adopted__snail" data-no="${esc(snail.no)}">
      ${trip(snail)}
    </button>`).join('');
  // A photo with no copy in the theme falls back to its own link (once).
  list.addEventListener('error', (event) => {
    const img = event.target;
    if (img.tagName !== 'IMG' || !img.dataset.fallback) return;
    const next = img.dataset.fallback;
    img.dataset.fallback = '';
    img.src = next;
  }, true);
  list.addEventListener('click', (event) => {
    const button = event.target.closest('[data-no]');
    const spot = button && places.find((p) => p.nos.includes(button.dataset.no));
    if (!spot) return;
    chosen = { places: [spot] }; say();
    fly({ lng: spot.lng, lat: spot.lat, zoom: Math.max(view.zoom, 6) });
    if (!wide.matches) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // Shown under the Snail heading, put away everywhere else (initFilters calls this).
  const wide = window.matchMedia('(min-width: 750px)');
  let grid = null;
  const place = () => {
    if (wide.matches || !grid) document.body.append(root); else grid.before(root);
    if (wide.matches) document.body.append(head); else root.append(head);   // phones: the heading is part of the block that stays under the menu, right under the globe
    list.classList.toggle('has-fixed-head', wide.matches);
  };
  wide.addEventListener('change', () => { if (on && shown) place(); });
  // Under the Snail heading there are two pages to choose from, written under the word itself:
  // "Find Snail" — the snails for sale, as on every other heading — and "Snail Map" — the
  // globe with the list of snails that have found a home. The choice is kept while the visitor
  // moves around the site.
  const pick = document.createElement('p');
  pick.className = 'snailpick';
  pick.innerHTML = [['find', 'Find Snail'], ['map', 'Snail Map']].map(([value, name]) => `
    <button type="button" class="filter" data-snail-view="${value}" aria-pressed="false"><span class="filter__mark" aria-hidden="true">(<span class="filter__dot">●</span>)</span><span>${name}</span></button>`).join('');
  let mode = 'find', shown = false;
  const mapOn = (yes) => {
    if (yes === shown) { if (yes) { place(); grid.append(list); } return; }
    shown = yes;
    if (!yes) { root.classList.remove('is-on'); root.hidden = true; root.remove(); list.remove(); head.remove(); clearInterval(blink); blink = 0; return; }
    place();
    grid.append(list);
    root.hidden = false;
    void root.offsetHeight;
    root.classList.add('is-on');
    chosen = null; say();
    load();
    // Arrive from the whole globe, a quarter turn away, and settle over the snails.
    Object.assign(view, { lng: home.lng - 110, lat: 24, zoom: 1 });
    fly({ lng: home.lng, lat: home.lat, zoom: 1 }, 1800);
  };
  const setMode = () => {
    pick.querySelectorAll('[data-snail-view]').forEach((button) => {
      const yes = button.dataset.snailView === mode;
      button.classList.toggle('is-active', yes);
      button.setAttribute('aria-pressed', yes);
    });
    grid.classList.toggle('is-map', mode === 'map');
    mapOn(mode === 'map');
  };
  // Changing page is a cross-fade, the same on every device: what is there fades out, the page
  // is changed and taken back to its top while nothing shows, and the new page fades in.
  let changing = 0;
  pick.addEventListener('click', (event) => {
    const button = event.target.closest('[data-snail-view]');
    if (!button || !grid || button.dataset.snailView === mode) return;
    mode = button.dataset.snailView;
    // The page name answers at once; the page itself follows.
    pick.querySelectorAll('[data-snail-view]').forEach((b) => {
      b.classList.toggle('is-active', b.dataset.snailView === mode);
      b.setAttribute('aria-pressed', b.dataset.snailView === mode);
    });
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const leaving = [grid, root, head];
    leaving.forEach((el) => { el.style.transition = 'opacity 0.26s ease'; el.style.opacity = '0'; });
    clearTimeout(changing);
    changing = setTimeout(() => {
      if (!grid) return;
      setMode();
      const handled = !grid.dispatchEvent(new CustomEvent('scatter:filtered', { cancelable: true }));
      if (!handled) window.scrollTo(0, 0);
      void grid.offsetHeight;
      leaving.forEach((el) => { el.style.transition = 'opacity 0.55s cubic-bezier(.625, .05, 0, 1)'; el.style.opacity = ''; });
      changing = setTimeout(() => leaving.forEach((el) => { el.style.transition = ''; }), 650);
    }, still ? 0 : 270);
  });
  window.MOT.snailMap = (show, forGrid) => {
    grid = show ? forGrid : null;
    on = show;
    if (!show) { pick.remove(); mapOn(false); document.querySelectorAll('.scatter.is-map').forEach((el) => el.classList.remove('is-map')); return; }
    document.body.append(pick);   // fixed under the menu on every screen, outside the page that scrolls
    setMode();
  };
}

/* ---------- Phone menu ----------
   On a phone the menu is one button at the top left — two short lines — and the bag at the top
   right. The button opens a sheet of the page colour with everything on it: the shop's headings
   (All, then each heading), then About and the footer's pages. A press on a name closes the
   sheet and goes there; the button, turned into a cross, closes it too. The links themselves
   are read from the menus already on the page, so nothing is kept twice. */
function initBurger() {
  let button = document.querySelector('.burger');
  if (!button) {
    button = document.createElement('button');
    button.type = 'button';
    button.className = 'burger';
    button.setAttribute('aria-label', 'Menu');
    button.setAttribute('aria-expanded', 'false');
    button.innerHTML = '<span></span><span></span>';
    const sheet = document.createElement('nav');
    sheet.className = 'burgermenu';
    sheet.setAttribute('aria-label', 'Menu');
    sheet.hidden = true;
    document.body.append(button, sheet);
    const close = () => {
      document.documentElement.classList.remove('menu-open');
      button.setAttribute('aria-expanded', 'false');
      setTimeout(() => { if (!document.documentElement.classList.contains('menu-open')) sheet.hidden = true; }, 350);
    };
    const open = () => {
      const nav = document.querySelector('[data-filters]');
      const home = nav?.dataset.home || '/';
      const here = new URLSearchParams(location.search).get('type');
      const onGrid = !!document.querySelector('[data-scatter]');
      const active = onGrid ? (document.querySelector('.snailpick [data-row-pick].is-active')?.dataset.rowPick || document.querySelector('.site-header .filter.is-active')?.dataset.filter || here || 'all') : null;
      const types = [['all', 'All']];
      (nav?.dataset.rowTypes || nav?.dataset.types || '').split(',').forEach((pair) => {
        const [value, label] = pair.split(':');
        if (value && value.trim()) types.push([value.trim(), (label || value).trim()]);
      });
      const pages = [...document.querySelectorAll('.site-header__menu a:not(.bag-link), .site-footer__menu > a:not(.site-footer__about)')]
        .map((a) => ({ href: a.getAttribute('href'), name: a.textContent.trim(), on: a.classList.contains('is-current') }));
      sheet.innerHTML = `<ul class="burgermenu__group">${types.map(([value, label]) => `<li><button type="button" class="filter${value === active ? ' is-active' : ''}" data-menu-type="${value}"><span class="filter__mark" aria-hidden="true">(<span class="filter__dot">●</span>)</span><span>${label}</span></button></li>`).join('')}</ul>
        <ul class="burgermenu__group">${pages.map((page) => `<li><a class="filter${page.on ? ' is-active' : ''}" href="${page.href}"><span class="filter__mark" aria-hidden="true">(<span class="filter__dot">●</span>)</span><span>${page.name}</span></a></li>`).join('')}</ul>`;
      sheet.hidden = false;
      void sheet.offsetHeight;
      document.documentElement.classList.add('menu-open');
      button.setAttribute('aria-expanded', 'true');
      sheet.onclick = (event) => {
        const type = event.target.closest('[data-menu-type]');
        const link = event.target.closest('a[href]');
        if (!type && !link) return;
        event.preventDefault();
        close();
        if (link) { if (window.MOT.go) window.MOT.go(new URL(link.getAttribute('href'), location.href).href); else location.href = link.href; return; }
        const value = type.dataset.menuType;
        // On the shop page the grid is filtered in place, through the buttons that already do it.
        const inPlace = document.querySelector(`.snailpick [data-row-pick="${value}"]`) || (document.querySelector('[data-scatter]') && document.querySelector(`.site-header .filter[data-filter="${value}"]`));
        if (inPlace) { inPlace.click(); window.scrollTo(0, 0); return; }
        const url = value === 'all' ? home : `${home}${home.includes('?') ? '&' : '?'}type=${encodeURIComponent(value)}`;
        if (window.MOT.go) window.MOT.go(new URL(url, location.href).href); else location.href = url;
      };
    };
    button.addEventListener('click', () => (document.documentElement.classList.contains('menu-open') ? close() : open()));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
    window.matchMedia('(min-width: 750px)').addEventListener('change', close);
    button._close = close;
  }
  button._close?.();   // a new page always starts with the menu closed
}

/* ---------- The ants ----------
   A short line of small black ants, one behind the other, going where they like across the
   page: the first one strolls — meandering, stopping, changing its mind — and the rest keep
   to the very path it took, the way ants follow a trail, each at its own distance. They walk on the page
   itself: scroll, and they stay where they were on it. They pay
   the pointer no attention until it comes close; then the one it nears shies away from it,
   and comes back to its place in the line once it is left alone. On a touch screen a tap
   near them does the same, and scatters them.
   The walk is the real one: three feet down at a time (front and hind of one side with the
   middle of the other) and each foot stays where it was put on the page while the body moves
   over it, the leg folding and stretching at the knee; then it lifts and reaches forward.
   They take no clicks, and do not come at all for visitors who asked for less motion. */
function initAnt() {
  if (window.MOT.ant) { if (window.MOT.antTurn) window.MOT.antTurn(); return; }   // a new page: the ants stay where they are on the screen
  window.MOT.ant = true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const COUNT = window.innerWidth < 750 ? 5 : 8, GAP = 14, U = 0.62;      // ants; px between them; px per body unit
  const STRIDE = 3.6, STANCE = 0.6, CYCLE = STRIDE / STANCE;               // step length (body units); share of a cycle a foot is down; body travel per cycle
  const PHONE = window.innerWidth < 750 || window.matchMedia('(pointer: coarse)').matches;
  const INK = PHONE ? '#000' : '#191614', BOLD = PHONE ? 1.6 : 1.4;   // on a phone: pure black and heavier lines, or the thin legs wash out to grey
  const NEAR = 95, SCARE = 72;                                             // how close the first ant comes to the pointer; how close the pointer may come to an ant
  const canvas = document.createElement('canvas');
  canvas.className = 'ant';
  canvas.setAttribute('aria-hidden', 'true');
  const ctx = canvas.getContext('2d');
  const TAU = Math.PI * 2, rnd = (a, b) => a + Math.random() * (b - a);
  const turnTo = (from, to) => Math.atan2(Math.sin(to - from), Math.cos(to - from));
  let dpr = 1, W = 0, H = 0;
  // The drawing sheet has exactly as many pixels as the screen shows it with: measured from the
  // sheet itself, not from the window — on a phone the two differ as the browser's bars come and
  // go, and a sheet stretched to fit is what makes the ants look coarse.
  let sx = 1, sy = 1;
  const size = () => {
    const box = canvas.getBoundingClientRect();
    dpr = Math.min(3, window.devicePixelRatio || 1); W = box.width || window.innerWidth; H = box.height || window.innerHeight;
    const w = Math.round(W * dpr), h = Math.round(H * dpr);
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    sx = w / W; sy = h / H;
  };
  // The sheet lies inside a floor that is exactly as long as the page and cuts off anything
  // beyond it, so the sheet can never make the page longer than its content.
  const floor = document.createElement('div');
  floor.className = 'antfloor';
  floor.append(canvas);
  document.body.append(floor);
  size();
  window.addEventListener('resize', size);
  if (window.ResizeObserver) new ResizeObserver(() => { const w = canvas.width, h = canvas.height; size(); if (canvas.width !== w || canvas.height !== h) paint(); }).observe(canvas);   // resizing wipes the sheet: draw again at once, not a frame later

  // The ants walk on the page, not on the glass: everything about them is kept in page
  // coordinates and the sheet is laid over whatever part of the page is in view. Where the page
  // scrolls natively (phones, tablets, most pages) the sheet lies in the page itself and is slid
  // to the part in view, so it moves with the scroll to the pixel; where the home page scrolls
  // by moving <main> (smoothScroll), the sheet stays put and the drawing is shifted instead.
  const root = document.documentElement;
  let view = 0, shift = 0, drawn = -1, mode = '', gone = 0;
  const place = () => {
    const virtual = root.classList.contains('has-virtual-scroll');
    if (virtual) {
      const main = document.querySelector('main'), moved = main && /translate3d\([^,]+,\s*(-?[\d.]+)px/.exec(main.style.transform);
      view = moved ? -parseFloat(moved[1]) : 0; shift = view;
    } else {
      view = window.scrollY;
      shift = view;
    }
    const now = virtual ? 'fixed' : 'absolute';
    if (now !== mode) { mode = now; canvas.style.position = now; }
    const top = virtual ? '0px' : `${shift}px`;
    if (canvas.style.top !== top) canvas.style.top = top;
    const long = `${Math.floor(Math.max(document.body.getBoundingClientRect().height, window.innerHeight))}px`;
    if (floor.style.height !== long) floor.style.height = long;
  };
  place();

  let turned = 0;
  window.MOT.antTurn = () => { turned = performance.now() + 1500; };

  let pointer = null;
  const point = (event) => { pointer = { x: event.clientX, y: event.clientY, t: performance.now(), touch: event.pointerType === 'touch' }; };
  window.addEventListener('pointermove', point, { passive: true });
  window.addEventListener('pointerdown', point, { passive: true });

  // The first ant, and the trail it leaves: points with the distance walked up to each.
  const y0 = rnd(0.25, 0.75) * H;
  const lead = { x: -24, y: y0, heading: 0, turn: 0, go: 0 };
  let trail = [];
  let walked = 0;
  // each ant: how far it has stepped off the trail, and what its legs are doing
  // Its place in the line is not a fixed step behind the one in front: some follow close, some
  // hang back, and each drifts a little nearer and further — and a touch to either side — as it goes.
  let back = 0;
  const ants = Array.from({ length: COUNT }, (_, n) => {
    if (n) back += GAP * rnd(0.85, 1.7);
    return { ox: 0, oy: 0, vx: 0, vy: 0, x: lead.x - back, y: y0, heading: 0, phase: rnd(0, 10), pace: 0, back, f: rnd(0.012, 0.03), ph: rnd(0, 6.3), f2: rnd(0.008, 0.02), ph2: rnd(0, 6.3) };
  });
  const LONG = back + 60;                                                    // how much trail to keep
  let t = 0, last = 0, raf = 0, tick = 0;
  let roam = null;
  // a point outside one of the four edges, picked at random: `out` px beyond it
  const beyond = (out) => {
    const edge = Math.floor(rnd(0, 4));
    return [{ x: rnd(0.1, 0.9) * W, y: view - out }, { x: W + out, y: view + rnd(0.1, 0.9) * H }, { x: rnd(0.1, 0.9) * W, y: view + H + out }, { x: -out, y: view + rnd(0.1, 0.9) * H }][edge];
  };
  // the line comes in: from somewhere off the page, in single file, toward a spot on it
  function enter() {
    const from = beyond(24);
    roam = { x: rnd(0.2, 0.8) * W, y: view + rnd(0.2, 0.8) * H, wait: 0 };
    lead.x = from.x; lead.y = from.y; lead.turn = 0; lead.go = 0;
    lead.heading = Math.atan2(roam.y - from.y, roam.x - from.x);
    const cos = Math.cos(lead.heading), sin = Math.sin(lead.heading);
    walked = LONG; trail = [];
    for (let d = 0; d <= walked; d += 2) trail.push({ x: lead.x - cos * (walked - d), y: lead.y - sin * (walked - d), s: d });
    ants.forEach((a) => { a.ox = a.oy = a.vx = a.vy = 0; a.x = lead.x - cos * a.back; a.y = lead.y - sin * a.back; a.heading = lead.heading; a.pace = 0; });
  }
  enter();

  // where on the trail an ant belongs, `back` px behind the first one
  const at = (back) => {
    const s = walked - back;
    for (let i = trail.length - 1; i > 0; i -= 1) {
      const a = trail[i - 1], b = trail[i];
      if (a.s <= s) { const k = b.s > a.s ? (s - a.s) / (b.s - a.s) : 0; return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k }; }
    }
    return { x: trail[0].x, y: trail[0].y };
  };

  // Long legs, as the black house ant has: each well over the length of its chest, the hind pair
  // longest of all, trailing far behind.
  // Legs: hip x, where the foot rests (x, and reach to the side), which three it steps with, and
  // a small lag so the three feet of a set do not land in the same instant. Each leg has a fixed
  // length — thigh and shin — and the knee is found from where the hip and the foot are.
  const LEGS = [[1.4, 4.5, 3.8, 0, 0], [0.5, 0.4, 5.0, 1, 0.05], [-0.4, -5.6, 4.2, 0, 0.1]].map(([hip, fx, reach, group, lag]) => {
    const full = Math.hypot(Math.abs(fx - hip) + STRIDE / 2, reach - 0.5) * 1.05;
    return { hip, fx, reach, group, lag, thigh: full * 0.44, shin: full * 0.56 };
  });
  function draw(a, n) {
    ctx.save();
    const step = (((a.phase / U) / CYCLE) % 1 + 1) % 1, amp = Math.min(1, a.pace);
    ctx.translate(a.x, a.y); ctx.rotate(a.heading + Math.sin(step * TAU) * 0.05 * amp); ctx.scale(U, U);   // the body swings a little from side to side as it steps
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    [1, -1].forEach((side) => {
      LEGS.forEach((leg) => {
        const p = (step + ((leg.group ? 1 : 0) ^ (side > 0 ? 0 : 1) ? 0.5 : 0) + leg.lag) % 1;
        let dx, lift = 0;
        if (p < STANCE) dx = STRIDE * (0.5 - p / STANCE);                                   // down: the page carries it back under the body
        else { const q = (p - STANCE) / (1 - STANCE); dx = STRIDE * (q * q * (3 - 2 * q) - 0.5); lift = Math.sin(q * Math.PI); }   // up: reaching forward
        const hx = leg.hip, hy = side * 0.4;
        const fx = leg.fx + dx * amp, fy = side * (leg.reach - lift * 0.5 * amp);          // a lifted foot is drawn in a little, as it looks from above
        const d = Math.min(Math.hypot(fx - hx, fy - hy), (leg.thigh + leg.shin) * 0.999), to = Math.atan2(fy - hy, fx - hx);
        const bend = Math.acos(Math.max(-1, Math.min(1, (leg.thigh * leg.thigh + d * d - leg.shin * leg.shin) / (2 * leg.thigh * d))));
        const k1 = [hx + Math.cos(to + bend) * leg.thigh, hy + Math.sin(to + bend) * leg.thigh], k2 = [hx + Math.cos(to - bend) * leg.thigh, hy + Math.sin(to - bend) * leg.thigh];
        const knee = side * k1[1] > side * k2[1] ? k1 : k2;                                 // the knee is the one that points away from the body
        ctx.lineWidth = 0.4 * BOLD; ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(knee[0], knee[1]); ctx.stroke();
        ctx.lineWidth = 0.31 * BOLD; ctx.beginPath(); ctx.moveTo(knee[0], knee[1]); ctx.lineTo(fx, fy); ctx.stroke();
        const sx = fx - knee[0], sy = fy - knee[1], sl = Math.hypot(sx, sy) || 1, tx = sx / sl + 0.7, ty = sy / sl, tl = Math.hypot(tx, ty) || 1;
        ctx.lineWidth = 0.24 * BOLD; ctx.beginPath(); ctx.moveTo(fx, fy); ctx.lineTo(fx + (tx / tl) * 0.9, fy + (ty / tl) * 0.9); ctx.stroke();   // the foot, turned forward
      });
      // Antennae of the small black house ant (the long-horned "crazy" ant): very long and hair-thin,
      // with no club. The first part (the scape) is one stiff rod, a good one and a half times the
      // length of the head, held out and forward; at its end the elbow; then the whip (the
      // funiculus), longer still, turned straight ahead, so the two run forward side by side. The ant can only
      // swing the rod and hinge the whip at the elbow — the whip itself does not bend at will — so
      // that is all that moves here: the two sides tap in turn, quicker while it walks.
      const quick = a.pace > 0.3, beat = t * (quick ? 14 : 8) + n * 1.7 + (side > 0 ? 0 : Math.PI);
      // They also lift and dip: an antenna raised off the ground points up toward the eye, so from
      // above it is seen shorter; dipped to touch the ground it lies at its full length. Each one
      // rises and falls on its own slow, uneven rhythm, sweeping to the side as it comes up.
      const lift = 0.5 + 0.5 * Math.sin(beat * 0.43 + n * 2.1) * Math.sin(beat * 0.17 + n);        // 0 on the ground … 1 held high
      // never still: a quick flick of the whip rides on a slower sweep of the whole antenna, and a fine tremble on both
      const swing = Math.sin(beat * 0.5 + n) * 0.3 + Math.sin(beat * 1.3 + n * 3) * 0.08 + lift * 0.25;
      const tap = Math.sin(beat) * 0.5 + Math.sin(beat * 2.3 + n) * 0.18 + lift * 0.3;
      const bx = 4.45, by = side * 0.45, rod = 0.56 + swing, whip = 0.0 + tap;   // angles from straight ahead, to this side
      const rodLong = 3.4 * (1 - lift * 0.18), whipLong = 4.5 * (1 - lift * 0.42);
      const ex = bx + Math.cos(rod) * rodLong, ey = by + side * Math.sin(rod) * rodLong;
      const tx = ex + Math.cos(whip) * whipLong, ty = ey + side * Math.sin(whip) * whipLong;
      ctx.lineWidth = 0.3 * BOLD; ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(ex, ey); ctx.stroke();
      ctx.lineWidth = 0.23 * BOLD; ctx.beginPath(); ctx.moveTo(ex, ey); ctx.quadraticCurveTo((ex + tx) / 2, (ey + ty) / 2 - side * 0.18, tx, ty); ctx.stroke();
    });
    // The body of the black house ant is slender, not plump. From the front: a head longer than
    // it is wide and a little broader at the back; a thin neck; a long narrow chest, pinched in
    // the middle so it reads as two lobes; the waist with its one small knot; and an abdomen
    // shaped like a drop — widest just behind the waist's end, drawn out to a point.
    ctx.lineWidth = 0.42 * BOLD; ctx.beginPath(); ctx.moveTo(-2.6, 0); ctx.lineTo(3.0, 0); ctx.stroke();    // what joins the parts: the neck and the waist
    const part = (cx, rx, ry) => { ctx.beginPath(); ctx.ellipse(cx, 0, rx, ry, 0, 0, TAU); ctx.fill(); };
    ctx.beginPath(); ctx.moveTo(-2.1, 0);                                                    // abdomen
    ctx.bezierCurveTo(-2.5, 2.1, -5.0, 2.45, -7.5, 0); ctx.bezierCurveTo(-5.0, -2.45, -2.5, -2.1, -2.1, 0); ctx.fill();
    part(-1.55, 0.36, 0.4);                                                                 // the knot of the waist
    part(-0.35, 1.0, 0.7); part(1.15, 1.1, 0.9);                                         // chest: the hind lobe, the fore lobe
    ctx.beginPath(); ctx.moveTo(4.85, 0);                                                    // head
    ctx.bezierCurveTo(4.85, 0.85, 3.9, 1.3, 3.1, 1.26); ctx.bezierCurveTo(2.3, 1.18, 2.25, 0.45, 2.25, 0);
    ctx.bezierCurveTo(2.25, -0.45, 2.3, -1.18, 3.1, -1.26); ctx.bezierCurveTo(3.9, -1.3, 4.85, -0.85, 4.85, 0); ctx.fill();
    // the faint gleam a black ant has on its abdomen and head
    ctx.fillStyle = PHONE ? 'rgba(255, 255, 255, .1)' : 'rgba(255, 255, 255, .16)';
    ctx.beginPath(); ctx.ellipse(-4.0, -0.45, 1.0, 0.3, 0, 0, TAU); ctx.fill();
    ctx.beginPath(); ctx.ellipse(3.4, -0.35, 0.5, 0.22, 0, 0, TAU); ctx.fill();
    ctx.fillStyle = INK;
    ctx.restore();
  }

  // somewhere to stroll to: anywhere on the page, but not right under a resting pointer
  function stroll() {
    if (Math.random() < 0.14) return { ...beyond(LONG + 70), wait: 0, leave: true };       // now and then: off the page altogether, the whole line
    let spot;
    for (let tries = 0; tries < 6; tries += 1) {
      spot = { x: rnd(0.06, 0.94) * W, y: view + rnd(0.1, 0.9) * H, wait: 0 };
      if (!pointer || pointer.touch || Math.hypot(spot.x - pointer.x, spot.y - pointer.y - view) > SCARE * 2.2) break;
    }
    return spot;
  }

  function frame(now) {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(0.05, (now - last) / 1000 || 0); last = now;
    t += dt; tick += 1;
    const was = view;
    place();
    // The page under them was changed, or thrown back to its top — another page opened, a filter
    // chosen: that is not scrolling, and the ants are not left behind by it. They keep their
    // place on the screen and go on walking on the new page.
    if (view !== was && (now < turned || Math.abs(view - was) > H * 0.6)) {
      const by = view - was;
      lead.y += by; roam.y += by; trail.forEach((spot) => { spot.y += by; }); ants.forEach((a) => { a.y += by; });
    }
    // What frightens them: the mouse wherever it rests, and a finger for a moment after it touches
    // — a tap scatters them more widely than a pointer passing by.
    // The fright of a tap does not switch on and off: it rises in a moment and ebbs away over a
    // second and a half, so they start off gently and drift back instead of jerking.
    const age = pointer ? now - pointer.t : 0;
    const ebb = pointer && pointer.touch ? Math.min(1, age / 180) * Math.max(0, 1 - age / 1600) ** 1.5 : 1;
    const shy = pointer && ebb > 0
      ? { x: pointer.x, y: pointer.y + view, r: pointer.touch ? SCARE * 1.7 : SCARE, push: (pointer.touch ? 620 : 700) * ebb, k: ebb } : null;
    let goal;
    // they go where they like; the pointer is nothing to them until it comes close
    if (roam.wait > 0) { roam.wait -= dt; if (roam.wait <= 0) { if (roam.leave) enter(); else roam = stroll(); } }   // gone for a while, then back in from another side
    else if (!roam.leave && Math.random() < dt * 0.07) roam = stroll();                                  // a change of mind on the way
    goal = roam;
    const dx = goal.x - lead.x, dy = goal.y - lead.y, far = Math.hypot(dx, dy);
    const fear = shy ? Math.hypot(shy.x - lead.x, shy.y - lead.y) : Infinity;
    const afraid = !!shy && shy.k > 0.3 && fear < shy.r;
    const arrived = !afraid && far < (goal === shy ? NEAR : 20);
    // Off the page: away for a while. On it: mostly straight on to somewhere else, now and then a
    // short stop — long stops, with the whole line standing still, read as the page having frozen.
    if (arrived && goal === roam && roam.wait <= 0) { if (roam.leave) roam.wait = rnd(3, 16); else if (Math.random() < 0.25) roam.wait = rnd(0.3, 1); else roam = stroll(); }
    if (afraid && Math.hypot(roam.x - shy.x, roam.y - shy.y) < SCARE * 2.2) roam = stroll();   // not toward what frightened it
    lead.go += ((arrived ? 0 : 1) - lead.go) * Math.min(1, dt * (afraid ? 8 : 5));
    if (lead.go > 0.02) {
      const want = afraid ? Math.atan2(lead.y - shy.y, lead.x - shy.x) : Math.atan2(dy, dx);
      lead.turn += rnd(-1, 1) * dt * (goal === roam ? 11 : 5); lead.turn *= 1 - Math.min(1, dt * (goal === roam ? 1.6 : 3));            // a little wander in the walk
      const most = afraid ? 3.6 : 2.2;
      lead.heading += (Math.max(-most, Math.min(most, turnTo(lead.heading, want) * (goal === roam && !afraid ? 1.2 : 5))) + lead.turn) * dt;
      const speed = afraid ? 30 + (1 - fear / shy.r) * 22 : 13 + Math.min(8, far * 0.03);   // a slow walk; a run when frightened
      const v = speed * lead.go * dt;
      lead.x += Math.cos(lead.heading) * v; lead.y += Math.sin(lead.heading) * v; walked += v;
      if (walked - trail[trail.length - 1].s >= 1.5) trail.push({ x: lead.x, y: lead.y, s: walked });
      while (trail.length > 2 && trail[1].s < walked - LONG) trail.shift();
    }
    let moving = lead.go > 0.02;
    ants.forEach((a, n) => {
      let x, y;
      if (!n) { x = lead.x; y = lead.y; }
      else {
        const where = a.back + Math.sin(walked * a.f + a.ph) * GAP * 0.2, home = at(where), ahead = at(where - 3);
        const side = Math.sin(walked * a.f2 + a.ph2) * 2.2, along = Math.hypot(ahead.x - home.x, ahead.y - home.y) || 1;
        const nx = -(ahead.y - home.y) / along, ny = (ahead.x - home.x) / along;
        home.x += nx * side; home.y += ny * side;
        let ax = -a.ox * 3.2 - a.vx * 4.4, ay = -a.oy * 3.2 - a.vy * 4.4;                    // drawn back to its place in the line
        if (shy) {
          const px = home.x + a.ox - shy.x, py = home.y + a.oy - shy.y, d = Math.hypot(px, py) || 1;
          if (d < shy.r) { const push = (1 - d / shy.r) * shy.push; ax += (px / d) * push; ay += (py / d) * push; }   // and driven off by the pointer
        }
        a.vx += ax * dt; a.vy += ay * dt;
        const fast = Math.hypot(a.vx, a.vy); if (fast > 62) { a.vx *= 62 / fast; a.vy *= 62 / fast; }   // an ant can only run so fast
        a.ox += a.vx * dt; a.oy += a.vy * dt;
        x = home.x + a.ox; y = home.y + a.oy;
      }
      const mx = x - a.x, my = y - a.y, moved = Math.hypot(mx, my), speed = dt ? moved / dt : 0;
      a.x = x; a.y = y; a.phase += moved;
      a.pace += (Math.min(1.5, speed / 9) - a.pace) * Math.min(1, dt * 5);
      if (!n) a.heading = lead.heading;
      else if (speed > 4) a.heading += turnTo(a.heading, Math.atan2(my, mx)) * Math.min(1, dt * 5.5);
      if (speed > 1) moving = true;
    });
    // left behind by the scrolling, a screen or more away: after a little while a line comes in where the visitor now is
    if (!roam.leave && (lead.y < view - H * 0.7 - LONG || lead.y > view + H * 1.7 + LONG)) { gone += dt; if (gone > 2.5) { gone = 0; enter(); } } else gone = 0;
    paint();
  }
  function paint() {
    drawn = shift;
    ctx.setTransform(sx, 0, 0, sy, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.translate(0, -shift);
    ctx.fillStyle = ctx.strokeStyle = INK;
    ants.forEach(draw);
  }
  const start = () => { cancelAnimationFrame(raf); raf = 0; if (!document.hidden) { last = performance.now(); raf = requestAnimationFrame(frame); } };
  document.addEventListener('visibilitychange', start);
  start();
}
